"use client"

import React, { useState, useEffect, useMemo } from 'react'
import { API_BASE_URL } from '../../config/api'
import { Image } from '../../components/common'
import { normalizeSearchResults, NormalizedSearchResult, normalizeRoomType } from '../../utils/normalizeApiResponse'
import { resolveAccommodationId } from '../utils/resolveAccommodationId'
import { WidgetConfig, SearchResult, WidgetBranding } from '../types'
import { applyBranding, injectCustomCSS } from '../utils/branding'
import { Card, CardContent } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Search, MessageCircle, Send, Mic, Calendar, Users, Award, Heart, Share2, X, ArrowLeft, Plus, Minus, BookOpen, ExternalLink, ArrowRight, Globe, ChevronDown, Clock, FileText, Hash, ChevronUp, Sparkles, Layers } from 'lucide-react'
import PaymentModal from './PaymentModal'
import BookingConfirmation from './BookingConfirmation'

// Branding-aware style helpers -- fallback to default purple when no branding is set.
// Defined at module level so all sub-components (BookingModal, SafariExplorePage, etc.) can use them.
const brandStyles = {
  primaryBg: { backgroundColor: 'var(--widget-primary-color, #7c3aed)' } as React.CSSProperties,
  primaryText: { color: 'var(--widget-primary-color, #7c3aed)' } as React.CSSProperties,
  primaryBorder: { borderColor: 'var(--widget-primary-color, #7c3aed)' } as React.CSSProperties,
  primaryGradient: {
    background: 'linear-gradient(to right, var(--widget-primary-color, #7c3aed), var(--widget-accent-color, var(--widget-primary-color, #4f46e5)))',
  } as React.CSSProperties,
  primaryBgLight: {
    backgroundColor: 'color-mix(in srgb, var(--widget-primary-color, #7c3aed) 10%, white)',
  } as React.CSSProperties,
  primaryBgLight15: {
    backgroundColor: 'color-mix(in srgb, var(--widget-primary-color, #7c3aed) 15%, white)',
  } as React.CSSProperties,
  primaryBorderLight: {
    borderColor: 'color-mix(in srgb, var(--widget-primary-color, #7c3aed) 30%, white)',
  } as React.CSSProperties,
} as const;

// Booking Modal Component
interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomType?: any;
  hotelName?: string;
  packageDetails?: any;
  activityDetails?: any;
  itemName?: string;
  itemType: 'room' | 'package' | 'activity';
  onConfirmBooking: (bookingData: any) => void;
  preSelectedDates?: {
    checkIn: string;
    checkOut: string;
    guests: number;
  } | null;
}

function BookingModal({ isOpen, onClose, roomType, hotelName, packageDetails, activityDetails, itemName, itemType, onConfirmBooking, preSelectedDates }: BookingModalProps) {
  const [checkInDate, setCheckInDate] = useState('')
  const [checkOutDate, setCheckOutDate] = useState('')
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  const [infants, setInfants] = useState(0)
  const [roomQuantity, setRoomQuantity] = useState(1)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('')

  // Initialize form with pre-selected dates from DateSelectionModal
  useEffect(() => {
    if (preSelectedDates && isOpen) {
      setCheckInDate(preSelectedDates.checkIn)
      setCheckOutDate(preSelectedDates.checkOut)
      setAdults(preSelectedDates.guests)
    }
  }, [preSelectedDates, isOpen])
  
  // Passenger Details
  const [passengerDetails, setPassengerDetails] = useState({
    primaryGuest: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      nationality: ''
    },
    additionalGuests: [] as Array<{
      firstName: string;
      lastName: string;
      dateOfBirth: string;
      nationality: string;
    }>
  })

  // Generate time slots for activities (every 2 hours from 6 AM to 6 PM)
  const generateTimeSlots = () => {
    const slots = []
    for (let hour = 6; hour <= 18; hour += 2) {
      const time = `${hour.toString().padStart(2, '0')}:00`
      const timeLabel = hour < 12 ? `${hour}:00 AM` : 
                       hour === 12 ? '12:00 PM' :
                       `${hour - 12}:00 PM`
      slots.push({ value: time, label: timeLabel })
    }
    return slots
  }

  const timeSlots = generateTimeSlots()

  // Update additional guests when guest count changes
  const updateAdditionalGuests = (newAdults: number, newChildren: number) => {
    const totalAdditional = Math.max(0, (newAdults + newChildren) - 1) // Exclude primary guest
    const currentAdditional = passengerDetails.additionalGuests.length
    
    if (totalAdditional > currentAdditional) {
      // Add more guest forms
      const newGuests = Array(totalAdditional - currentAdditional).fill(null).map(() => ({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        nationality: ''
      }))
      setPassengerDetails(prev => ({
        ...prev,
        additionalGuests: [...prev.additionalGuests, ...newGuests]
      }))
    } else if (totalAdditional < currentAdditional) {
      // Remove excess guest forms
      setPassengerDetails(prev => ({
        ...prev,
        additionalGuests: prev.additionalGuests.slice(0, totalAdditional)
      }))
    }
  }

  // Get the item details based on type
  const currentItem = itemType === 'room' ? roomType : itemType === 'package' ? packageDetails : activityDetails
  const displayName = itemType === 'room' ? roomType?.name : 
                     itemType === 'package' ? packageDetails?.name || itemName :
                     activityDetails?.name || itemName
  const basePrice = itemType === 'room' ? (roomType?.effective_price || roomType?.base_price) : 
                   itemType === 'package' ? packageDetails?.base_price :
                   activityDetails?.price || activityDetails?.price_per_person || activityDetails?.base_price || activityDetails?.pricing?.base || activityDetails?.pricing?.price_per_person
  const locationName = itemType === 'room' ? hotelName : 
                      itemType === 'package' ? packageDetails?.location :
                      activityDetails?.location

  // Calculate nights and total price
  const calculateNights = () => {
    if (!checkInDate || !checkOutDate) return 0
    const checkIn = new Date(checkInDate)
    const checkOut = new Date(checkOutDate)
    const diffTime = Math.abs(checkOut.getTime() - checkIn.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  const nights = calculateNights()
  const totalPrice = itemType === 'room' 
    ? nights * (basePrice || 0) * roomQuantity 
    : itemType === 'package'
    ? (basePrice || 0) * (adults + children) // Package price per person
    : (basePrice || 0) * (adults + children) // Activity price per person

  const handleConfirmBooking = () => {
    const bookingData = {
      itemType,
      roomType: itemType === 'room' ? roomType : null,
      packageDetails: itemType === 'package' ? packageDetails : null,
      activityDetails: itemType === 'activity' ? activityDetails : null,
      itemName: displayName,
      locationName,
      checkInDate,
      checkOutDate,
      guests: { adults, children, infants },
      roomQuantity: itemType === 'room' ? roomQuantity : 1,
      nights: itemType === 'room' ? nights : 
              itemType === 'package' ? packageDetails?.duration_days || nights :
              Math.ceil(activityDetails?.duration_hours / 24) || 1, // Activity duration in days
      totalPrice,
      selectedTimeSlot: itemType === 'activity' ? selectedTimeSlot : null,
      passengerDetails: passengerDetails,
      guestName: `${passengerDetails.primaryGuest.firstName} ${passengerDetails.primaryGuest.lastName}`.trim(),
      guestEmail: passengerDetails.primaryGuest.email,
      guestPhone: passengerDetails.primaryGuest.phone
    }
    onConfirmBooking(bookingData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Book Your Stay</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="w-8 h-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Item Info */}
        <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'color-mix(in srgb, var(--widget-primary-color, #7c3aed) 10%, white)' }}>
          <h3 className="font-semibold text-gray-900">{displayName}</h3>
          <p className="text-sm text-gray-600">{locationName}</p>
          <p className="text-lg font-bold" style={{ color: 'var(--widget-primary-color, #7c3aed)' }}>
            ${basePrice}{itemType === 'room' ? '/night' : '/person'}
          </p>
          {itemType === 'package' && packageDetails?.duration_days && (
            <p className="text-sm text-gray-500">{packageDetails.duration_days} days duration</p>
          )}
          {itemType === 'activity' && activityDetails?.duration_hours && (
            <p className="text-sm text-gray-500">{activityDetails.duration_hours} hours duration</p>
          )}
        </div>

        {/* Date Selection */}
        <div className="mb-6">
          {itemType === 'activity' ? (
            // Single date for activities
            <>
              <h4 className="font-semibold text-gray-900 mb-3">Select Date</h4>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date</label>
                <Input
                  type="date"
                  value={checkInDate}
                  onChange={(e) => {
                    setCheckInDate(e.target.value)
                    setCheckOutDate(e.target.value) // Set same date for both for consistency
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="border-gray-300 w-full"
                />
              </div>
            </>
          ) : (
            // Check-in/Check-out dates for rooms and packages
            <>
              <h4 className="font-semibold text-gray-900 mb-3">Select Dates</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Check-in</label>
                  <Input
                    type="date"
                    value={checkInDate}
                    onChange={(e) => setCheckInDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Check-out</label>
                  <Input
                    type="date"
                    value={checkOutDate}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                    min={checkInDate || new Date().toISOString().split('T')[0]}
                    className="border-gray-300"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Time Slot Selection - Only show for activities */}
        {itemType === 'activity' && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Select Time Slot</h4>
            <div className="grid grid-cols-2 gap-2">
              {timeSlots.map((slot) => (
                <Button
                  key={slot.value}
                  variant={selectedTimeSlot === slot.value ? "default" : "outline"}
                  size="sm"
                  className={`${
                    selectedTimeSlot === slot.value
                      ? "text-white"
                      : "border-gray-300"
                  }`}
                  style={selectedTimeSlot === slot.value ? { backgroundColor: 'var(--widget-primary-color, #7c3aed)' } : undefined}
                  onClick={() => setSelectedTimeSlot(slot.value)}
                >
                  {slot.label}
                </Button>
              ))}
            </div>
            {selectedTimeSlot && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: {timeSlots.find(slot => slot.value === selectedTimeSlot)?.label}
              </p>
            )}
          </div>
        )}

        {/* Guest Selection */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Guests</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Adults</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newAdults = Math.max(1, adults - 1);
                    setAdults(newAdults);
                    updateAdditionalGuests(newAdults, children);
                  }}
                  className="w-8 h-8 p-0"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-8 text-center">{adults}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newAdults = Math.min(10, adults + 1);
                    setAdults(newAdults);
                    updateAdditionalGuests(newAdults, children);
                  }}
                  className="w-8 h-8 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Children</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newChildren = Math.max(0, children - 1);
                    setChildren(newChildren);
                    updateAdditionalGuests(adults, newChildren);
                  }}
                  className="w-8 h-8 p-0"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-8 text-center">{children}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newChildren = Math.min(10, children + 1);
                    setChildren(newChildren);
                    updateAdditionalGuests(adults, newChildren);
                  }}
                  className="w-8 h-8 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Infants</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInfants(Math.max(0, infants - 1))}
                  className="w-8 h-8 p-0"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-8 text-center">{infants}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setInfants(Math.min(10, infants + 1))}
                  className="w-8 h-8 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Passenger Details */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-4">Passenger Details</h4>
          
          {/* Primary Guest */}
          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
            <h5 className="font-medium text-gray-800 mb-3">Primary Guest</h5>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                placeholder="First Name*"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
                value={passengerDetails.primaryGuest.firstName}
                onChange={(e) => setPassengerDetails(prev => ({
                  ...prev,
                  primaryGuest: { ...prev.primaryGuest, firstName: e.target.value }
                }))}
                required
              />
              <input
                type="text"
                placeholder="Last Name*"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
                value={passengerDetails.primaryGuest.lastName}
                onChange={(e) => setPassengerDetails(prev => ({
                  ...prev,
                  primaryGuest: { ...prev.primaryGuest, lastName: e.target.value }
                }))}
                required
              />
            </div>
            <div className="grid grid-cols-1 gap-3 mb-3">
              <input
                type="email"
                placeholder="Email Address*"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
                value={passengerDetails.primaryGuest.email}
                onChange={(e) => setPassengerDetails(prev => ({
                  ...prev,
                  primaryGuest: { ...prev.primaryGuest, email: e.target.value }
                }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="tel"
                placeholder="Phone Number*"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
                value={passengerDetails.primaryGuest.phone}
                onChange={(e) => setPassengerDetails(prev => ({
                  ...prev,
                  primaryGuest: { ...prev.primaryGuest, phone: e.target.value }
                }))}
                required
              />
              <input
                type="date"
                placeholder="Date of Birth"
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
                value={passengerDetails.primaryGuest.dateOfBirth}
                onChange={(e) => setPassengerDetails(prev => ({
                  ...prev,
                  primaryGuest: { ...prev.primaryGuest, dateOfBirth: e.target.value }
                }))}
              />
            </div>
          </div>

          {/* Additional Guests */}
          {passengerDetails.additionalGuests.map((guest, index) => (
            <div key={index} className="mb-3 p-3 border rounded-lg">
              <h6 className="font-medium text-gray-700 mb-2">Guest {index + 2}</h6>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="First Name*"
                  className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ring-[var(--widget-primary-color,#7c3aed)]"
                  value={guest.firstName}
                  onChange={(e) => {
                    const updatedGuests = [...passengerDetails.additionalGuests];
                    updatedGuests[index] = { ...guest, firstName: e.target.value };
                    setPassengerDetails(prev => ({
                      ...prev,
                      additionalGuests: updatedGuests
                    }));
                  }}
                  required
                />
                <input
                  type="text"
                  placeholder="Last Name*"
                  className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ring-[var(--widget-primary-color,#7c3aed)]"
                  value={guest.lastName}
                  onChange={(e) => {
                    const updatedGuests = [...passengerDetails.additionalGuests];
                    updatedGuests[index] = { ...guest, lastName: e.target.value };
                    setPassengerDetails(prev => ({
                      ...prev,
                      additionalGuests: updatedGuests
                    }));
                  }}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  placeholder="Date of Birth"
                  className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ring-[var(--widget-primary-color,#7c3aed)]"
                  value={guest.dateOfBirth}
                  onChange={(e) => {
                    const updatedGuests = [...passengerDetails.additionalGuests];
                    updatedGuests[index] = { ...guest, dateOfBirth: e.target.value };
                    setPassengerDetails(prev => ({
                      ...prev,
                      additionalGuests: updatedGuests
                    }));
                  }}
                />
                <input
                  type="text"
                  placeholder="Nationality"
                  className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ring-[var(--widget-primary-color,#7c3aed)]"
                  value={guest.nationality}
                  onChange={(e) => {
                    const updatedGuests = [...passengerDetails.additionalGuests];
                    updatedGuests[index] = { ...guest, nationality: e.target.value };
                    setPassengerDetails(prev => ({
                      ...prev,
                      additionalGuests: updatedGuests
                    }));
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Room Quantity - Only show for room bookings */}
        {itemType === 'room' && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Number of Rooms</h4>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Rooms</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRoomQuantity(Math.max(1, roomQuantity - 1))}
                  className="w-8 h-8 p-0"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-8 text-center">{roomQuantity}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRoomQuantity(Math.min(10, roomQuantity + 1))}
                  className="w-8 h-8 p-0"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Summary */}
        {(itemType === 'room' ? nights > 0 : true) && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Booking Summary</h4>
            <div className="space-y-1 text-sm">
              {itemType === 'room' ? (
                <>
                  <div className="flex justify-between">
                    <span>Nights:</span>
                    <span>{nights}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rooms:</span>
                    <span>{roomQuantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate per night:</span>
                    <span>${basePrice}</span>
                  </div>
                </>
              ) : itemType === 'package' ? (
                <>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span>{packageDetails?.duration_days} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Travelers:</span>
                    <span>{adults + children} people</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate per person:</span>
                    <span>${basePrice}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span>{activityDetails?.duration_hours} hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Participants:</span>
                    <span>{adults + children} people</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Rate per person:</span>
                    <span>${basePrice}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                <span>Total:</span>
                <span>${totalPrice}</span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBooking}
            disabled={
              !checkInDate || 
              (itemType !== 'activity' && (!checkOutDate || nights <= 0)) ||
              (itemType === 'activity' && !selectedTimeSlot) ||
              !passengerDetails.primaryGuest.firstName ||
              !passengerDetails.primaryGuest.lastName ||
              !passengerDetails.primaryGuest.email ||
              !passengerDetails.primaryGuest.phone ||
              passengerDetails.additionalGuests.some(guest => 
                !guest.firstName || !guest.lastName
              )
            }
            className="flex-1 text-white"
            style={{ backgroundColor: 'var(--widget-primary-color, #7c3aed)' }}
          >
            Confirm Booking
          </Button>
        </div>
      </div>
    </div>
  )
}

// Safari Explore Page Component - EXACT from your original code
// Date Selection Modal Component
interface DateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomType: any;
  hotelName: string;
  selectedDates: {
    checkIn: string;
    checkOut: string;
    guests: number;
  } | null;
  updatedPricing: any | null;
  loadingPricing: boolean;
  onDateSelection: (checkIn: string, checkOut: string, guests: number) => void;
  onProceedToBooking: () => void;
}

function DateSelectionModal({ 
  isOpen, 
  onClose, 
  roomType, 
  hotelName, 
  selectedDates, 
  updatedPricing, 
  loadingPricing,
  onDateSelection,
  onProceedToBooking 
}: DateSelectionModalProps) {
  const [checkInDate, setCheckInDate] = useState('')
  const [checkOutDate, setCheckOutDate] = useState('')
  const [guests, setGuests] = useState(2)

  if (!isOpen) return null

  const handleDateSubmit = () => {
    if (!checkInDate || !checkOutDate) {
      alert('Please select both check-in and check-out dates')
      return
    }
    onDateSelection(checkInDate, checkOutDate, guests)
  }

  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  const getTomorrowDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Select Your Dates</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <h4 className="font-semibold text-gray-700 mb-2">{roomType?.name} at {hotelName}</h4>
          <p className="text-sm text-gray-600">
            Rate: ${roomType?.effective_price || roomType?.base_price}/night
            {roomType?.effective_price && roomType?.effective_price !== roomType?.base_price && (
              <span className="ml-2 font-medium" style={{ color: 'var(--widget-primary-color, #7c3aed)' }}>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" style={{ color: 'var(--widget-primary-color, #7c3aed)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  Special rate applied
                </div>
              </span>
            )}
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Check-in Date
            </label>
            <input
              type="date"
              value={checkInDate}
              onChange={(e) => setCheckInDate(e.target.value)}
              min={getTodayDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Check-out Date
            </label>
            <input
              type="date"
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
              min={checkInDate || getTomorrowDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Guests
            </label>
            <select
              value={guests}
              onChange={(e) => setGuests(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ring-[var(--widget-primary-color,#7c3aed)]"
            >
              {[1,2,3,4,5,6].map(num => (
                <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Show pricing if dates are selected */}
        {selectedDates && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h5 className="font-medium text-gray-800 mb-2">Selected Dates</h5>
            <p className="text-sm text-gray-600">
              {selectedDates.checkIn} to {selectedDates.checkOut} • {selectedDates.guests} guests
            </p>
            
            {loadingPricing ? (
              <div className="mt-3 text-center">
                <div className="inline-flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" style={{ color: 'var(--widget-primary-color, #7c3aed)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Checking availability and pricing...
                </div>
              </div>
            ) : updatedPricing ? (
              <div className="mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Rate for selected dates:</span>
                  <span className="font-semibold text-lg" style={{ color: 'var(--widget-primary-color, #7c3aed)' }}>
                    ${updatedPricing.effective_price}/night
                  </span>
                </div>
                {updatedPricing.has_dynamic_pricing && (
                  <p className="text-sm text-orange-600 mt-1">
                    <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" style={{ color: 'var(--widget-primary-color, #7c3aed)' }} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  Special rate applied
                </div> for these dates
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-3 text-center text-red-600">
                <p className="text-sm">Unable to load pricing for selected dates</p>
              </div>
            )}
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {!selectedDates ? (
            <button
              onClick={handleDateSubmit}
              disabled={!checkInDate || !checkOutDate}
              className="flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--widget-primary-color, #7c3aed)' }}
            >
              Check Availability
            </button>
          ) : (
            <button
              onClick={onProceedToBooking}
              disabled={loadingPricing || !updatedPricing}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Proceed to Booking
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- GuidesPage helpers ---

function formatTitle(item: any): string {
  const raw = item.metadata?.og_title || item.details?.name || item.metadata?.title || item.source_url || 'Guide';
  if (raw.startsWith('http')) {
    try {
      const url = new URL(raw);
      const segments = url.pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        return segments[segments.length - 1]
          .replace(/[-_]/g, ' ')
          .replace(/\.\w+$/, '')
          .split(' ')
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      }
      return url.hostname.replace('www.', '');
    } catch { return raw; }
  }
  return raw;
}

function extractTopic(item: any): string {
  const title = item.details?.name || item.metadata?.title || '';
  if (title && !title.startsWith('http')) {
    return title.substring(0, 50).trim();
  }
  const text = item.text || item.details?.description || '';
  const firstSentence = text.split(/[.!?]/)[0] || '';
  return firstSentence.substring(0, 50).trim();
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
}

// Warm editorial gradient palette (Safari Journal inspired)
const heroGradients = [
  'linear-gradient(135deg, #2d1b00 0%, #4a2c0a 40%, #6b3a10 100%)',
  'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
  'linear-gradient(135deg, #1b2e1a 0%, #2d4a2c 40%, #3d6b3a 100%)',
  'linear-gradient(135deg, #2e1a2d 0%, #4a2c4a 40%, #6b3a6b 100%)',
  'linear-gradient(135deg, #1a2e2e 0%, #2c4a4a 40%, #3a6b6b 100%)',
];

// Get full text from a crawled content item (try all possible fields)
function getFullText(item: any): string {
  return (item.text || item.details?.description || item.metadata?.text || '').trim();
}

// Universal content analyzer — no domain-specific keywords
function analyzeText(text: string): {
  wordCount: number;
  readingTimeMin: number;
  sentenceCount: number;
  hasQuestions: boolean;
  keyPhrases: string[];
  contentType: 'faq' | 'list' | 'narrative' | 'article';
} {
  const words = text.split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const readingTimeMin = Math.max(1, Math.round(wordCount / 200));
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 5);
  const sentenceCount = sentences.length;
  const questionMarks = (text.match(/\?/g) || []).length;
  const hasQuestions = questionMarks >= 2;

  // Extract key phrases: capitalized multi-word phrases found mid-sentence
  const commonWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their', 'we', 'our', 'you', 'your', 'not', 'no', 'so', 'if', 'as', 'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'than', 'too', 'very', 'just', 'about', 'also', 'into', 'over', 'after', 'before', 'between', 'under', 'again', 'there', 'here', 'when', 'where', 'why', 'how', 'what', 'which', 'who', 'whom']);
  const phraseMatches = text.match(/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+/g) || [];
  const keyPhrases = Array.from(new Set(
    phraseMatches
      .filter(p => !commonWords.has(p.split(' ')[0].toLowerCase()))
      .slice(0, 4)
  ));

  // Determine content type
  let contentType: 'faq' | 'list' | 'narrative' | 'article' = 'article';
  if (hasQuestions) contentType = 'faq';
  else if ((text.match(/^\s*[-•*\d]+[.)]\s/gm) || []).length >= 3) contentType = 'list';
  else if (sentenceCount >= 5 && wordCount >= 80) contentType = 'narrative';

  return { wordCount, readingTimeMin, sentenceCount, hasQuestions, keyPhrases, contentType };
}

// Build 2-4 info cards from universal analysis — always returns at least 2 cards
function buildContentCards(item: any, analysis: ReturnType<typeof analyzeText>): { icon: typeof Clock; label: string; value: string }[] {
  const cards: { icon: typeof Clock; label: string; value: string }[] = [];
  const sourceUrl = item.source_url || item.metadata?.source_url || '';

  // 1. Reading time (always)
  cards.push({
    icon: Clock,
    label: `${analysis.readingTimeMin} min read`,
    value: `${analysis.wordCount} words`,
  });

  // 2. Source breadcrumb (always if URL exists)
  if (sourceUrl) {
    try {
      const url = new URL(sourceUrl);
      const segments = url.pathname.split('/').filter(Boolean);
      const breadcrumb = segments.length > 0
        ? segments.slice(0, 2).map(s => s.replace(/[-_]/g, ' ')).join(' / ')
        : url.hostname.replace('www.', '');
      cards.push({ icon: Globe, label: 'Source', value: breadcrumb });
    } catch {
      cards.push({ icon: Globe, label: 'Source', value: 'Web' });
    }
  }

  // 3. Content type label
  const typeLabels: Record<string, string> = {
    faq: 'FAQ Guide',
    list: 'Checklist',
    narrative: 'Deep Dive',
    article: 'Editorial',
  };
  cards.push({
    icon: FileText,
    label: 'Type',
    value: typeLabels[analysis.contentType] || 'Article',
  });

  // 4. Key phrases / topics (conditional — only if we found any)
  if (analysis.keyPhrases.length > 0) {
    cards.push({
      icon: Hash,
      label: 'Topics',
      value: analysis.keyPhrases.slice(0, 3).join(', '),
    });
  }

  return cards.slice(0, 4);
}

// Split text into display paragraphs — no truncation
function formatEditorialParagraphs(text: string): string[] {
  // Try natural paragraph breaks first
  const byDoubleNewline = text.split(/\n\n+/).map(p => p.trim()).filter(p => p.length > 20);
  if (byDoubleNewline.length >= 2) return byDoubleNewline;

  // Fallback: group ~3 sentences per paragraph
  const sentences = text.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 5);
  if (sentences.length <= 3) return [text.trim()];

  const paragraphs: string[] = [];
  for (let i = 0; i < sentences.length; i += 3) {
    paragraphs.push(sentences.slice(i, i + 3).join(' '));
  }
  return paragraphs;
}

// Pick icon based on content type and index for visual variety
function guideIcon(analysis: ReturnType<typeof analyzeText>, index: number) {
  const icons = [BookOpen, Globe, FileText, Layers, Sparkles];
  if (analysis.contentType === 'faq') return BookOpen;
  if (analysis.contentType === 'list') return FileText;
  return icons[index % icons.length];
}

// Content type labels for badges
const contentTypeLabels: Record<string, string> = {
  faq: 'FAQ Guide',
  list: 'Checklist',
  narrative: 'Deep Dive',
  article: 'Editorial',
};

function GuidesPage({
  crawledContent,
  searchQuery,
  isLoading,
  onBrowsePackages,
  brandStyles: bs,
}: {
  crawledContent: any[];
  searchQuery: string;
  isLoading: boolean;
  onBrowsePackages: (topic: string) => void;
  brandStyles: { primaryBg: React.CSSProperties; primaryGradient: React.CSSProperties; primaryBgLight: React.CSSProperties; primaryText: React.CSSProperties };
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [ogImages, setOgImages] = useState<Record<string, string>>({});

  // Filter out irrelevant results — use original_score (pre-fusion) when
  // available, since fusion weighting can halve the score.
  const filtered = crawledContent
    .filter(item => (item.original_score ?? item.score ?? 1) >= 0.30)
    .sort((a, b) => (b.original_score ?? b.score ?? 0) - (a.original_score ?? a.score ?? 0));

  // Fetch og:image for items that don't have one in metadata
  useEffect(() => {
    const urlsToFetch = filtered
      .map(item => item.source_url || item.metadata?.source_url || '')
      .filter(url => url && !ogImages[url])
      .filter(url => !filtered.some(item =>
        (item.source_url === url || item.metadata?.source_url === url) && item.metadata?.og_image
      ));

    if (urlsToFetch.length === 0) return;

    const fetchOgImages = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/og-metadata`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(urlsToFetch.slice(0, 10)),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const newImages: Record<string, string> = {};
        for (const [url, meta] of Object.entries(data)) {
          if ((meta as any).og_image) {
            newImages[url] = (meta as any).og_image;
          }
        }
        if (Object.keys(newImages).length > 0) {
          setOgImages(prev => ({ ...prev, ...newImages }));
        }
      } catch {
        // Silently ignore — fallback to gradient
      }
    };
    fetchOgImages();
  }, [filtered.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-4xl mx-auto">
      {/* Skeleton loading — warm editorial shimmer */}
      {isLoading && crawledContent.length === 0 && (
        <div className="px-5 py-6 space-y-5" style={{ backgroundColor: '#faf8f5' }}>
          {/* Skeleton: attribution */}
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full guide-skeleton" />
            <div className="h-3 w-40 rounded-full guide-skeleton" />
          </div>
          {/* Skeleton: hero card */}
          <div className="max-w-[90%] mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(84,67,60,0.06)]">
              <div className="h-[220px] guide-skeleton" />
              <div className="bg-white p-5 space-y-3">
                <div className="h-5 rounded-lg w-3/4 guide-skeleton" />
                <div className="h-3 rounded w-full guide-skeleton" />
                <div className="h-3 rounded w-5/6 guide-skeleton" />
                <div className="h-4 rounded w-28 guide-skeleton mt-1" />
              </div>
            </div>
          </div>
          {/* Skeleton: accent card */}
          <div className="max-w-[85%] mr-auto">
            <div className="rounded-2xl bg-white p-5 space-y-3 shadow-[0_8px_24px_rgba(84,67,60,0.05)]" style={{ borderLeft: '4px solid #f0ede8' }}>
              <div className="flex justify-between">
                <div className="h-3 w-16 rounded-full guide-skeleton" />
                <div className="h-3 w-20 rounded-full guide-skeleton" />
              </div>
              <div className="h-5 rounded-lg w-2/3 guide-skeleton" />
              <div className="h-3 rounded w-full guide-skeleton" />
              <div className="h-3 rounded w-4/5 guide-skeleton" />
            </div>
          </div>
          {/* Skeleton: split card */}
          <div className="max-w-[85%] ml-auto">
            <div className="rounded-2xl overflow-hidden bg-white flex shadow-[0_8px_24px_rgba(84,67,60,0.05)]">
              <div className="w-2/5 h-[160px] guide-skeleton" />
              <div className="w-3/5 p-4 space-y-2.5">
                <div className="h-3 w-16 rounded-full guide-skeleton" />
                <div className="h-4 rounded-lg w-3/4 guide-skeleton" />
                <div className="h-3 rounded w-full guide-skeleton" />
                <div className="h-3 rounded w-4/5 guide-skeleton" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state — warm editorial */}
      {!isLoading && filtered.length === 0 && (
        <div className="text-center py-20 px-8" style={{ backgroundColor: '#faf8f5' }}>
          <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#f0ede8' }}>
            <Layers className="w-7 h-7" style={{ color: '#87736b' }} />
          </div>
          <h3 className="text-lg font-bold" style={{ color: '#54433c' }}>No guides found</h3>
          <p className="text-sm mt-2 leading-relaxed max-w-[260px] mx-auto" style={{ color: '#87736b' }}>
            Try a different search to discover curated articles and travel guides
          </p>
        </div>
      )}

      {/* Content — editorial conversation flow */}
      {!(isLoading && crawledContent.length === 0) && filtered.length > 0 && (
        <div className="px-5 py-6 space-y-5" style={{ backgroundColor: '#faf8f5' }}>
          {/* Assistant attribution header */}
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ ...bs.primaryGradient, boxShadow: '0 4px 12px rgba(140,49,40,0.2)' }}>
              <BookOpen className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-[11px] font-medium" style={{ color: '#87736b' }}>
              Curated for &ldquo;{searchQuery || 'you'}&rdquo; &middot; just now
            </span>
          </div>

          {/* Cards — varied layout based on index */}
          {filtered.map((item, idx) => {
            const isExpanded = expandedIndex === idx;
            const fullText = getFullText(item);
            const itemSourceUrl = item.source_url || item.metadata?.source_url || '';
            const itemOgImage = item.metadata?.og_image || ogImages[itemSourceUrl] || '';
            const analysis = analyzeText(fullText);
            const paragraphs = formatEditorialParagraphs(fullText);
            const sourceUrl = item.source_url || item.metadata?.source_url || '';
            const IconComp = guideIcon(analysis, idx);
            const typeLabel = contentTypeLabels[analysis.contentType] || 'Article';

            // === HERO CARD (first item) ===
            if (idx === 0) {
              const heroImage = itemOgImage;
              return (
                <div key="guide-hero" className="max-w-[90%] mx-auto">
                  <div className="rounded-2xl overflow-hidden" style={{ boxShadow: '0 12px 40px rgba(84,67,60,0.08)' }}>
                    {/* Image or gradient area with badges */}
                    <div
                      className="relative h-[220px] overflow-hidden"
                      style={heroImage ? undefined : { background: heroGradients[0] }}
                    >
                      {/* Hero image if available */}
                      {heroImage ? (
                        <img
                          src={heroImage}
                          alt={formatTitle(item)}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to gradient on image load error
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            target.parentElement!.style.background = heroGradients[0];
                          }}
                        />
                      ) : (
                        <>
                          {/* Decorative pattern (gradient-only fallback) */}
                          <div className="absolute inset-0 guide-hero-pattern" style={{
                            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
                            backgroundSize: '60px 60px, 40px 40px',
                          }} />
                          {/* Large decorative icon */}
                          <IconComp className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 text-white/[0.07]" />
                        </>
                      )}
                      {/* Bottom gradient overlay — always shown for text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                      {/* Category badge */}
                      <span
                        className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.08em] text-white backdrop-blur-md"
                        style={{ ...bs.primaryBg, opacity: 0.92 }}
                      >
                        {typeLabel}
                      </span>
                      {/* Reading time — frosted glass */}
                      <span className="absolute top-4 right-4 text-[10px] text-white/90 font-semibold backdrop-blur-md px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}>
                        {analysis.readingTimeMin} min read
                      </span>
                      {/* Bottom title + source overlay */}
                      <div className="absolute bottom-0 left-0 right-0 px-5 pb-4 pt-10">
                        {sourceUrl && (
                          <span className="text-[10px] uppercase tracking-[0.08em] text-white/60 font-semibold block mb-1.5">
                            {extractDomain(sourceUrl)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* White card body */}
                    <div className="bg-white p-5 space-y-3">
                      <h3 className="text-xl font-extrabold leading-[1.2]" style={{ color: '#1c1c19' }}>
                        {formatTitle(item)}
                      </h3>
                      {/* Preview paragraph */}
                      <p className="text-sm leading-[1.7]" style={{ color: '#54433c' }}>
                        {paragraphs[0]}
                      </p>
                      {/* Expanded paragraphs — animated */}
                      {paragraphs.length > 1 && (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateRows: isExpanded ? '1fr' : '0fr',
                            transition: 'grid-template-rows 0.35s ease-out',
                          }}
                        >
                          <div style={{ overflow: 'hidden' }}>
                            <div className="space-y-3 pt-1" style={{
                              opacity: isExpanded ? 1 : 0,
                              transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
                              transition: 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s',
                            }}>
                              {paragraphs.slice(1).map((para, i) => (
                                <p key={`hero-p-${i}`} className="text-sm leading-[1.7]" style={{ color: '#54433c' }}>
                                  {para}
                                </p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Read more / Show less */}
                      {paragraphs.length > 1 && (
                        <button
                          className="flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.06em] hover:opacity-80 transition-opacity"
                          style={bs.primaryText}
                          onClick={() => setExpandedIndex(isExpanded ? null : 0)}
                        >
                          {isExpanded ? 'SHOW LESS' : 'READ MORE'}
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Source link */}
                      {sourceUrl && (
                        <a
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] hover:opacity-70 transition-opacity"
                          style={{ color: '#87736b' }}
                        >
                          <Globe className="w-3 h-3" />
                          {extractDomain(sourceUrl)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            // === ACCENT TEXT CARD (odd indices) ===
            if (idx % 2 !== 0) {
              return (
                <div key={`guide-accent-${idx}`} className="max-w-[85%] mr-auto">
                  <div
                    className="rounded-2xl bg-white overflow-hidden cursor-pointer guide-card-hover"
                    style={{ borderLeft: '4px solid', borderLeftColor: (bs.primaryBg as any)?.backgroundColor || '#7c3aed', boxShadow: '0 8px 24px rgba(84,67,60,0.06)' }}
                    onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                  >
                    <div className="p-5 space-y-2.5">
                      {/* Category + reading time */}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.08em] font-bold" style={bs.primaryText}>
                          {typeLabel}
                        </span>
                        <span className="text-[10px] font-semibold flex items-center gap-1" style={{ color: '#87736b' }}>
                          <Clock className="w-3 h-3" />
                          {analysis.readingTimeMin} min read
                        </span>
                      </div>
                      {/* Title row with decorative icon */}
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <h4 className="text-[17px] font-bold leading-snug" style={{ color: '#1c1c19' }}>
                            {formatTitle(item)}
                          </h4>
                          <p className={`text-sm leading-[1.7] mt-2 ${!isExpanded ? 'line-clamp-3' : ''}`} style={{ color: '#54433c' }}>
                            {paragraphs[0]}
                          </p>
                        </div>
                        <IconComp className="w-8 h-8 flex-shrink-0 opacity-10" style={bs.primaryText} />
                      </div>
                      {/* Expanded paragraphs — animated */}
                      {paragraphs.length > 1 && (
                        <div style={{
                          display: 'grid',
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                          transition: 'grid-template-rows 0.35s ease-out',
                        }}>
                          <div style={{ overflow: 'hidden' }}>
                            <div className="space-y-3 pt-1" style={{
                              opacity: isExpanded ? 1 : 0,
                              transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
                              transition: 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s',
                            }}>
                              {paragraphs.slice(1).map((para, i) => (
                                <p key={`acc-p-${i}`} className="text-sm leading-[1.7]" style={{ color: '#54433c' }}>
                                  {para}
                                </p>
                              ))}
                              {/* Metadata pills */}
                              <div className="flex flex-wrap gap-2 pt-2">
                                {buildContentCards(item, analysis).slice(0, 3).map((card, ci) => {
                                  const MetaIcon = card.icon;
                                  return (
                                    <span key={`ameta-${ci}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#f6f3ee', color: '#87736b' }}>
                                      <MetaIcon className="w-3 h-3" />
                                      {card.label}
                                    </span>
                                  );
                                })}
                              </div>
                              {/* Source link */}
                              {sourceUrl && (
                                <a
                                  href={sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-[11px] hover:opacity-70 transition-opacity pt-1"
                                  style={{ color: '#87736b' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Globe className="w-3 h-3" />
                                  Read full article at {extractDomain(sourceUrl)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* LEARN MORE / SHOW LESS CTA */}
                      <button
                        className="inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.06em] group"
                        style={bs.primaryText}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedIndex(isExpanded ? null : idx);
                        }}
                      >
                        {isExpanded ? 'SHOW LESS' : 'LEARN MORE'}
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            // === SPLIT CARD (even indices > 0) ===
            return (
              <div key={`guide-split-${idx}`} className="max-w-[85%] ml-auto">
                <div
                  className="rounded-2xl overflow-hidden bg-white cursor-pointer guide-card-hover"
                  style={{ boxShadow: '0 8px 24px rgba(84,67,60,0.06)' }}
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                >
                  <div className="flex">
                    {/* Left: image or gradient area */}
                    {itemOgImage ? (
                      <div className="w-2/5 flex-shrink-0 relative min-h-[160px] overflow-hidden">
                        <img
                          src={itemOgImage}
                          alt={formatTitle(item)}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            target.parentElement!.style.background = heroGradients[idx % heroGradients.length];
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="w-2/5 flex-shrink-0 flex flex-col items-center justify-center p-4 gap-2.5 min-h-[160px]"
                        style={{ background: heroGradients[idx % heroGradients.length] }}
                      >
                        <IconComp className="w-10 h-10 text-white/20" />
                        <span className="text-[9px] text-white/60 font-semibold uppercase tracking-[0.08em]">
                          {analysis.readingTimeMin} min read
                        </span>
                        {sourceUrl && (
                          <span className="text-[9px] text-white/40 font-medium text-center px-2">
                            {extractDomain(sourceUrl)}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Right: text content */}
                    <div className="w-3/5 p-4 space-y-2 flex flex-col justify-center">
                      <span className="text-[10px] uppercase tracking-[0.08em] font-bold" style={{ color: '#87736b' }}>
                        {typeLabel}
                      </span>
                      <h4 className="text-[15px] font-bold leading-snug line-clamp-2" style={{ color: '#1c1c19' }}>
                        {formatTitle(item)}
                      </h4>
                      <p className="text-xs leading-[1.7] line-clamp-3" style={{ color: '#54433c' }}>
                        {paragraphs[0]}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Expanded content below split card — animated */}
                <div style={{
                  display: 'grid',
                  gridTemplateRows: isExpanded ? '1fr' : '0fr',
                  transition: 'grid-template-rows 0.35s ease-out',
                }}>
                  <div style={{ overflow: 'hidden' }}>
                    <div className="bg-white rounded-b-2xl -mt-1 px-5 pb-5 pt-3 space-y-3" style={{
                      boxShadow: '0 8px 24px rgba(84,67,60,0.06)',
                      opacity: isExpanded ? 1 : 0,
                      transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
                      transition: 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s',
                    }}>
                      {paragraphs.slice(1).map((para, i) => (
                        <p key={`split-p-${i}`} className="text-sm leading-[1.7]" style={{ color: '#54433c' }}>
                          {para}
                        </p>
                      ))}
                      {/* Metadata pills */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        {buildContentCards(item, analysis).slice(0, 3).map((card, ci) => {
                          const MetaIcon = card.icon;
                          return (
                            <span key={`smeta-${ci}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#f6f3ee', color: '#87736b' }}>
                              <MetaIcon className="w-3 h-3" />
                              {card.label}
                            </span>
                          );
                        })}
                      </div>
                      {/* Source link */}
                      {sourceUrl && (
                        <a
                          href={sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] hover:opacity-70 transition-opacity"
                          style={{ color: '#87736b' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Globe className="w-3 h-3" />
                          Read full article at {extractDomain(sourceUrl)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Dual CTA section — after all cards */}
          <div className="max-w-[85%] mx-auto pt-3 space-y-3">
            <button
              className="w-full py-4 rounded-xl text-white font-bold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 hover:brightness-110"
              style={{ ...bs.primaryGradient, boxShadow: '0 8px 24px rgba(140,49,40,0.3)' }}
              onClick={() => onBrowsePackages(searchQuery || extractTopic(filtered[0]))}
            >
              Explore Packages
              <ArrowRight className="w-4 h-4" />
            </button>
            {(() => {
              const featuredUrl = filtered[0]?.source_url || filtered[0]?.metadata?.source_url || '';
              return featuredUrl ? (
                <a
                  href={featuredUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 hover:brightness-95"
                  style={{ backgroundColor: '#f6f3ee', color: '#54433c' }}
                >
                  Read Full Article
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <button
                  className="w-full py-4 rounded-xl font-bold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 hover:brightness-95"
                  style={{ backgroundColor: '#f6f3ee', color: '#54433c' }}
                  onClick={() => onBrowsePackages(searchQuery || extractTopic(filtered[0]))}
                >
                  View Details
                  <ArrowRight className="w-4 h-4" />
                </button>
              );
            })()}
            <p className="text-center text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: '#87736b' }}>
              {filtered.length} article{filtered.length !== 1 ? 's' : ''} curated for your search
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function SafariExplorePage({ onBooking, searchResults = [], crawledContent = [], tenantId, hasSearched = false, isLoading = false, branding, stripePublishableKey }: { onBooking?: any, searchResults?: any[], crawledContent?: any[], tenantId: string, hasSearched?: boolean, isLoading?: boolean, branding?: WidgetBranding | null, stripePublishableKey?: string }) {
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV === 'development') console.log('[SafariExplorePage] crawledContent:', crawledContent.length, 'hasSearched:', hasSearched);
  const [selectedPackage, setSelectedPackage] = useState<string>("")
  const [favorites, setFavorites] = useState<string[]>([])
  const [activeFilter, setActiveFilter] = useState<string>("all")
  const [currentImageIndexes, setCurrentImageIndexes] = useState<{[key: string]: number}>({})
  const [expandedDescriptions, setExpandedDescriptions] = useState<{[key: string]: boolean}>({})
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({})
  // Date selection modal state
  const [dateSelectionModal, setDateSelectionModal] = useState<{
    isOpen: boolean,
    roomType: any,
    hotelName: string,
    selectedDates: {
      checkIn: string,
      checkOut: string,
      guests: number
    } | null,
    updatedPricing: any | null,
    loadingPricing: boolean
  }>({
    isOpen: false,
    roomType: null,
    hotelName: '',
    selectedDates: null,
    updatedPricing: null,
    loadingPricing: false
  })

  const [bookingModal, setBookingModal] = useState<{
    isOpen: boolean, 
    roomType: any, 
    hotelName: string,
    packageDetails: any,
    activityDetails: any,
    itemName: string,
    itemType: 'room' | 'package' | 'activity',
    preSelectedDates?: {
      checkIn: string;
      checkOut: string;
      guests: number;
    } | null;
  }>({
    isOpen: false,
    roomType: null,
    hotelName: '',
    packageDetails: null,
    activityDetails: null,
    itemName: '',
    itemType: 'room',
    preSelectedDates: null
  })

  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean,
    bookingData: any
  }>({
    isOpen: false,
    bookingData: null
  })

  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean,
    bookingData: any,
    paymentData: any
  }>({
    isOpen: false,
    bookingData: null,
    paymentData: null
  })

  const toggleFavorite = (packageId: string) => {
    setFavorites((prev) => (prev.includes(packageId) ? prev.filter((id) => id !== packageId) : [...prev, packageId]))
  }

  const navigateImage = (packageId: string, direction: 'prev' | 'next', totalImages: number) => {
    setCurrentImageIndexes(prev => {
      const currentIndex = prev[packageId] || 0
      let newIndex
      if (direction === 'next') {
        newIndex = currentIndex >= totalImages - 1 ? 0 : currentIndex + 1
      } else {
        newIndex = currentIndex <= 0 ? totalImages - 1 : currentIndex - 1
      }
      return { ...prev, [packageId]: newIndex }
    })
  }

  const getCurrentImage = (pkg: any) => {
    const currentIndex = currentImageIndexes[pkg.id] || 0
    if (pkg.images && Array.isArray(pkg.images) && pkg.images.length > 0) {
      const image = pkg.images[currentIndex]

      // Handle both object format (with url property) and string format
      if (typeof image === 'object' && image.url) {
        const url = image.url.startsWith('http') ? image.url : `${API_BASE_URL}${image.url.startsWith('/') ? image.url : '/' + image.url}`
        return url
      } else if (typeof image === 'string') {
        const url = image.startsWith('http') ? image : `${API_BASE_URL}${image.startsWith('/') ? image : '/' + image}`
        return url
      }
    }
    return pkg.image
  }

  const toggleDescriptionExpansion = (packageId: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }))
  }

  const handleBookRoom = (roomType: any, hotelName: string) => {
    // Open date selection modal first
    setDateSelectionModal({
      isOpen: true,
      roomType,
      hotelName,
      selectedDates: null,
      updatedPricing: null,
      loadingPricing: false
    })
  }

  const handleDateSelection = async (checkIn: string, checkOut: string, guests: number) => {
    // Update state with selected dates and start loading
    setDateSelectionModal(prev => ({
      ...prev,
      selectedDates: { checkIn, checkOut, guests },
      loadingPricing: true,
      updatedPricing: null
    }))

    try {
      // Fetch updated pricing based on selected dates using centralized ID resolution
      const accommodationId = resolveAccommodationId(dateSelectionModal.roomType, selectedItem);

      if (accommodationId) {
        const response = await fetch(
          `${API_BASE_URL}/api/accommodations/${accommodationId}/room-types?tenant_id=${tenantId}&check_in=${checkIn}`
        )
        const data = await response.json()
        
        if (data.success && data.room_types) {
          // Find the matching room type with updated pricing
          const updatedRoomType = data.room_types.find((rt: any) => 
            rt.id === dateSelectionModal.roomType?.id
          )
          
          if (updatedRoomType) {
            setDateSelectionModal(prev => ({
              ...prev,
              updatedPricing: updatedRoomType,
              loadingPricing: false
            }))
          } else {
            console.error('Room type not found in updated pricing')
            setDateSelectionModal(prev => ({
              ...prev,
              loadingPricing: false
            }))
          }
        }
      } else {
        console.error('No accommodation ID found - cannot fetch pricing')
        // Stop loading since we can't make the API call
        setDateSelectionModal(prev => ({
          ...prev,
          loadingPricing: false
        }))
      }
    } catch (error) {
      console.error('Error fetching updated pricing:', error)
      setDateSelectionModal(prev => ({
        ...prev,
        loadingPricing: false
      }))
    }
  }

  const proceedToBooking = () => {
    // Close date selection modal and open booking modal with selected dates and updated pricing
    const { roomType, hotelName, selectedDates, updatedPricing } = dateSelectionModal
    
    setDateSelectionModal({
      isOpen: false,
      roomType: null,
      hotelName: '',
      selectedDates: null,
      updatedPricing: null,
      loadingPricing: false
    })

    setBookingModal({
      isOpen: true,
      roomType: updatedPricing || roomType, // Use updated pricing if available
      hotelName,
      packageDetails: null,
      activityDetails: null,
      itemName: roomType?.name || 'Unknown Room',
      itemType: 'room',
      preSelectedDates: selectedDates
    })
  }

  const handleBookPackage = (packageDetails: any, packageName: string) => {
    setBookingModal({
      isOpen: true,
      roomType: null,
      hotelName: '',
      packageDetails,
      activityDetails: null,
      itemName: packageName,
      itemType: 'package',
      preSelectedDates: null
    })
  }

  const handleBookActivity = (activityDetails: any, activityName: string) => {
    setBookingModal({
      isOpen: true,
      roomType: null,
      hotelName: '',
      packageDetails: null,
      activityDetails,
      itemName: activityName,
      itemType: 'activity',
      preSelectedDates: null
    })
  }

  const handleConfirmBooking = async (bookingData: any) => {
    try {
      // Create booking in backend first
      const bookingPayload = {
        tenant_id: tenantId,
        guest_info: {
          name: bookingData.guestName,
          email: bookingData.guestEmail,
          phone: bookingData.guestPhone,
          guests: bookingData.guests?.adults || 1
        },
        selected_items: [{
          content_type: bookingData.itemType === 'room' ? 'accommodation' : bookingData.itemType,
          item_id: (() => {
            const rawId = bookingData.itemType === 'room' 
              ? bookingData.roomType?.id 
              : bookingData.itemType === 'package'
              ? bookingData.packageDetails?.id
              : bookingData.activityDetails?.id;
            
            // Generate a valid UUID if the item_id is not a valid UUID
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            if (rawId && uuidRegex.test(rawId)) {
              return rawId;
            } else {
              // Generate a new UUID for demo/invalid IDs
              return crypto.randomUUID();
            }
          })(),
          item_name: bookingData.itemName,
          quantity: bookingData.roomQuantity || 1,
          price_per_unit: bookingData.totalPrice / (bookingData.roomQuantity || 1),
          total_price: bookingData.totalPrice,
          check_in_date: bookingData.checkInDate,
          check_out_date: bookingData.checkOutDate
        }],
        total_amount: bookingData.totalPrice,
        special_requests: '',
        passenger_details: bookingData.passengerDetails
      }

      const response = await fetch(`${API_BASE_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingPayload)
      })

      const result = await response.json()
      
      if (result.success) {
        // Add booking UUID and tenant ID for payment
        // Use UUID from confirmation_details (BookingAgent result), with fallback chain
        const enhancedBookingData = {
          ...bookingData,
          bookingId: result.confirmation_details?.booking_id || result.booking_id || result.booking_reference,
          bookingReference: result.booking_reference,
          tenantId: tenantId,
          currency: 'USD'
        }
        
        // Open payment modal with the created booking
        setPaymentModal({
          isOpen: true,
          bookingData: enhancedBookingData
        })
      } else {
        console.error('Booking creation failed:', result)
        alert(`Booking failed: ${result.message || result.detail || 'Please try again.'}`)
      }

    } catch (error) {
      console.error('Error creating booking:', error)
      alert('Unable to connect to the booking service. Please check your connection and try again.')
    }
  }

  const handlePaymentSuccess = (paymentResult: any) => {
    const bookingData = paymentModal.bookingData
    
    // Close payment modal
    setPaymentModal({
      isOpen: false,
      bookingData: null
    })
    
    // Show confirmation modal with booking and payment details
    setConfirmationModal({
      isOpen: true,
      bookingData: bookingData,
      paymentData: paymentResult
    })
    
    // Still call onBooking for parent component if needed
    if (onBooking) {
      const bookingType = bookingData.itemType || 'accommodation'
      const resourceId = bookingType === 'room' 
        ? bookingData.roomType?.id 
        : bookingType === 'package'
        ? bookingData.packageDetails?.id
        : bookingData.activityDetails?.id
      
      onBooking({
        id: bookingData.bookingId,
        type: bookingType === 'room' ? 'accommodation' : bookingType,
        resourceId: resourceId || 'unknown',
        itemName: bookingData.itemName,
        locationName: bookingData.locationName,
        guestName: bookingData.guestName,
        guestEmail: bookingData.guestEmail,
        checkInDate: bookingData.checkInDate,
        checkOutDate: bookingData.checkOutDate,
        guests: bookingData.guests,
        roomQuantity: bookingData.roomQuantity || 1,
        totalAmount: bookingData.totalPrice,
        currency: 'USD',
        status: 'confirmed',
        bookingType: bookingType
      })
    }
  }

  const handlePaymentError = (error: string) => {
    console.error('Payment failed:', error)
    // You can show an error message to the user here
    alert(`Payment failed: ${error}`)
  }

  const handlePaymentClose = () => {
    setPaymentModal({
      isOpen: false,
      bookingData: null
    })
  }


  // Default pricing for activities when no price is available
  const getDefaultActivityPrice = (result: any) => {
    if (result.type === 'activity' || result.content_type === 'activity') {
      // Provide realistic default pricing based on activity type or keywords
      const title = (result.title || result.metadata?.name || '').toLowerCase()
      
      if (title.includes('cruise') || title.includes('boat') || title.includes('seine')) {
        return 45 // River cruises
      } else if (title.includes('tower') || title.includes('eiffel') || title.includes('skip')) {
        return 35 // Skip-the-line tours
      } else if (title.includes('museum') || title.includes('louvre') || title.includes('art')) {
        return 25 // Museum entries
      } else if (title.includes('walking') || title.includes('tour') || title.includes('guide')) {
        return 30 // Walking tours
      } else if (title.includes('food') || title.includes('wine') || title.includes('dining')) {
        return 85 // Food experiences
      } else if (title.includes('day trip') || title.includes('excursion')) {
        return 120 // Full day trips
      } else {
        return 40 // General activities
      }
    }
    return 0 // Non-activities
  }

  const getImageForContentType = (contentType: string) => {
    // No fallback images - return null to show "No images available"
    return null;
  }

  // Helper function to validate image path
  // Backend provides full URLs - no URL construction needed
  const isValidImagePath = (path: string): boolean => {
    return !!(path &&
           typeof path === 'string' &&
           !path.includes('placeholder') &&
           !path.includes('unsplash.com') &&
           (path.includes('.jpg') || path.includes('.jpeg') || path.includes('.png') || path.includes('.webp')));
  };

  const getImageFromSearchResult = (result: NormalizedSearchResult | { details?: { images?: unknown[] }; metadata?: { images?: unknown[] } }) => {
    // Handle normalized results (have images as pre-parsed string array)
    if ('images' in result && Array.isArray(result.images) && result.images.length > 0) {
      return result.images[0];
    }

    // Handle raw/mock objects that have details or metadata with images
    const details = (result as { details?: { images?: unknown[] } }).details;
    const metadata = (result as { metadata?: { images?: unknown[] } }).metadata;

    // Check details.images
    if (details?.images && Array.isArray(details.images) && details.images.length > 0) {
      const firstImage = details.images[0];
      if (typeof firstImage === 'string' && isValidImagePath(firstImage)) {
        return firstImage;
      }
      if (typeof firstImage === 'object' && firstImage && 'url' in firstImage) {
        const url = (firstImage as { url?: string }).url;
        if (url && isValidImagePath(url)) {
          return url;
        }
      }
    }

    // Check metadata.images
    if (metadata?.images && Array.isArray(metadata.images) && metadata.images.length > 0) {
      const firstImage = metadata.images[0];
      if (typeof firstImage === 'string' && isValidImagePath(firstImage)) {
        return firstImage;
      }
      if (typeof firstImage === 'object' && firstImage && 'url' in firstImage) {
        const url = (firstImage as { url?: string }).url;
        if (url && isValidImagePath(url)) {
          return url;
        }
      }
    }

    // Fallback: Check direct image field
    const rawResult = result as unknown as Record<string, unknown>;
    if (rawResult.image && isValidImagePath(rawResult.image as string)) {
      return rawResult.image as string;
    }

    return null;
  }

  // Process image arrays - backend provides full URLs
  const processImageArray = (rawImages: any[]): string[] => {
    if (!Array.isArray(rawImages) || rawImages.length === 0) {
      return [];
    }

    const processedImages: string[] = [];

    rawImages.forEach((image) => {
      let imageUrl: string | null = null;

      // Object format (accommodations): {id, url, thumbnail_url, ...}
      if (typeof image === 'object' && image.url) {
        const url = image.url;
        if (url && typeof url === 'string' &&
            !url.includes('placeholder') &&
            !url.includes('unsplash.com') &&
            (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.webp'))) {
          imageUrl = url;
        }
      }
      // String format (packages): backend provides full URLs
      else if (typeof image === 'string') {
        if (image &&
            !image.includes('placeholder') &&
            !image.includes('unsplash.com') &&
            (image.includes('.jpg') || image.includes('.jpeg') || image.includes('.png') || image.includes('.webp'))) {
          imageUrl = image;
        }
      }

      if (imageUrl) {
        processedImages.push(imageUrl);
      }
    });

    return processedImages;
  }

  // Get all items from search results only (no mock data fallback)
  // Data is already normalized at fetch boundary - no JSON.parse needed
  const allItemsRaw = useMemo(() => {
    if (searchResults.length === 0) return [];

    // Defense-in-depth: filter crawled_content again at render boundary
    const safeResults = searchResults.filter(r => {
      const ct = (r.content_type || r.type || '').toLowerCase();
      return ct !== 'crawled_content';
    });

    return safeResults.map((result, index) => {
      // Use pre-normalized fields directly - images already parsed and validated
      const processedItem = {
        id: result.id || `result-${index}`,
        title: result.title,
        location: result.location,
        type: result.type || result.content_type || 'Package',
        content_type: result.content_type || result.type || 'Package',
        content_id: result.content_id || result.id,
        price: `Starting from $${result.price || getDefaultActivityPrice(result)}`,
        image: getImageFromSearchResult(result) || undefined,
        description: result.description,
        rating: result.rating,
        amenities: result.amenities,
        images: result.images, // Already normalized and validated
        metadata: result.metadata
      };

      return processedItem;
    });
  }, [searchResults]);

  // Filter items based on active filter
  const allItems = useMemo(() => {
    if (activeFilter === "all") {
      return allItemsRaw;
    }
    
    const filtered = allItemsRaw.filter(item => {
      const contentType = (item.content_type || item.type || '').toLowerCase();
      
      switch (activeFilter) {
        case "accommodation":
          return contentType === 'accommodation' || contentType === 'hotel';
        case "activity": 
          return contentType === 'activity' || contentType === 'experience';
        case "package":
          return contentType === 'package';
        case "transfer":
          return contentType === 'transfer' || contentType === 'transportation';
        case "guide":
          return contentType === 'guide' || contentType === 'guide_service';
        default:
          return true;
      }
    });

    return filtered;
  }, [allItemsRaw, activeFilter]);

  // Helper function to determine package type for badges
  const getPackageTypeBadge = (pkg: any) => {
    const title = pkg.title?.toLowerCase() || '';
    const description = pkg.description?.toLowerCase() || '';
    const content = `${title} ${description}`;
    
    if (content.includes('luxury') || content.includes('premium') || content.includes('deluxe')) {
      return { type: 'Luxury', color: 'bg-yellow-500 text-white', icon: '👑' };
    } else if (content.includes('family') || content.includes('kids') || content.includes('children')) {
      return { type: 'Family', color: 'bg-blue-500 text-white', icon: '👨‍👩‍👧‍👦' };
    } else if (content.includes('adventure') || content.includes('safari') || content.includes('wildlife')) {
      return { type: 'Adventure', color: 'bg-orange-500 text-white', icon: '🦁' };
    } else if (content.includes('cultural') || content.includes('heritage') || content.includes('historical')) {
      return { type: 'Cultural', color: 'bg-green-600 text-white', icon: '🏛️' };
    } else if (content.includes('romantic') || content.includes('honeymoon') || content.includes('couples')) {
      return { type: 'Romantic', color: 'bg-pink-500 text-white', icon: '💕' };
    } else {
      return { type: 'Experience', color: 'bg-purple-500 text-white', icon: '✨' };
    }
  };

  // Helper function to extract quick info from package data
  const getPackageQuickInfo = (pkg: any) => {
    const metadata = pkg.metadata || {};
    return {
      duration: metadata.duration || '5 days',
      groupSize: metadata.max_participants || '10 people',
      difficulty: metadata.difficulty || 'Moderate'
    };
  };

  const selectedItem = useMemo(() => 
    allItems.find(item => item.id === selectedPackage) || allItems[0], 
    [allItems, selectedPackage]
  );
  const [roomTypes, setRoomTypes] = useState<any[]>([])
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [packageDetails, setPackageDetails] = useState<any>(null)
  const [loadingPackage, setLoadingPackage] = useState(false)
  const [activityDetails, setActivityDetails] = useState<any>(null)
  const [loadingActivity, setLoadingActivity] = useState(false)
  
  // Set initial selection to first item if we have search results or filter changes
  useEffect(() => {
    if (allItems.length > 0 && allItems[0].id) {
      setSelectedPackage(allItems[0].id);
    } else if (allItems.length === 0) {
      setSelectedPackage("");
    }
  }, [allItems, activeFilter]); // Changed from searchResults to allItems

  // Get room types from search results when a hotel is selected
  useEffect(() => {
    if (selectedItem && selectedItem.type === 'accommodation') {
      setLoadingRooms(true);
      
      // Find the corresponding search result with details
      const searchResult = searchResults.find(result => 
        result.metadata?.content_id === selectedItem.metadata?.content_id
      );
      
      const roomTypesRaw = searchResult?.details?.room_types as unknown[] | undefined;
      if (roomTypesRaw && Array.isArray(roomTypesRaw)) {
        // Use normalizeRoomType to handle JSON parsing for amenities/images
        const processedRoomTypes = roomTypesRaw.map(normalizeRoomType);
        setRoomTypes(processedRoomTypes);
      } else {
        setRoomTypes([]);
      }
      
      setLoadingRooms(false);
    } else {
      setRoomTypes([]);
    }
  }, [selectedItem, searchResults]);

  // Fetch package details when a package is selected
  useEffect(() => {
    const fetchPackageDetails = async () => {
      const isPackage = selectedItem && (selectedItem.type === 'package' || selectedItem.content_type === 'package');
      const contentId = (selectedItem as any)?.content_id || (selectedItem as any)?.id;
      
      
      if (isPackage && contentId) {
        const currentTenantId = selectedItem.metadata?.tenant_id || tenantId;
        
        setLoadingPackage(true);
        try {
          const url = `${API_BASE_URL}/api/packages/${contentId}?tenant_id=${currentTenantId}`;

          const response = await fetch(url);
          const data = await response.json();

          if (data.success) {
            setPackageDetails(data.package);
          } else {
            console.error('Package API returned error:', data);
            setPackageDetails(null);
          }
        } catch (error) {
          console.error('Failed to fetch package details:', error);
          setPackageDetails(null);
        } finally {
          setLoadingPackage(false);
        }
      } else {
        setPackageDetails(null);
      }
    };

    // Only fetch if we have a selected package
    if (selectedItem?.type === 'package' || selectedItem?.content_type === 'package') {
      fetchPackageDetails();
    } else {
      setPackageDetails(null);
    }
  }, [(selectedItem as any)?.content_id, selectedItem?.metadata?.tenant_id, selectedItem?.type, selectedItem?.content_type, tenantId]);

  // Fetch activity details when an activity is selected
  useEffect(() => {
    const fetchActivityDetails = async () => {
      const isActivity = selectedItem && (selectedItem.type === 'activity' || selectedItem.content_type === 'activity');
      const contentId = (selectedItem as any)?.content_id || selectedItem?.metadata?.content_id;
      
      if (isActivity && contentId) {
        const currentTenantId = selectedItem.metadata?.tenant_id || tenantId;
        
        setLoadingActivity(true);
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/activities/${contentId}?tenant_id=${currentTenantId}`
          );
          const data = await response.json();
          if (data.success) {
            setActivityDetails(data.activity);
          }
        } catch (error) {
          console.error('Failed to fetch activity details:', error);
          setActivityDetails(null);
        } finally {
          setLoadingActivity(false);
        }
      } else {
        setActivityDetails(null);
      }
    };

    // Only fetch if we have a selected activity
    if (selectedItem?.type === 'activity' || selectedItem?.content_type === 'activity') {
      fetchActivityDetails();
    } else {
      setActivityDetails(null);
    }
  }, [(selectedItem as any)?.content_id, selectedItem?.metadata?.content_id, selectedItem?.metadata?.tenant_id, selectedItem?.type, selectedItem?.content_type, tenantId]);

  return (
    <>
      {/* Date Selection Modal */}
      <DateSelectionModal
        isOpen={dateSelectionModal.isOpen}
        onClose={() => setDateSelectionModal({
          isOpen: false,
          roomType: null,
          hotelName: '',
          selectedDates: null,
          updatedPricing: null,
          loadingPricing: false
        })}
        roomType={dateSelectionModal.roomType}
        hotelName={dateSelectionModal.hotelName}
        selectedDates={dateSelectionModal.selectedDates}
        updatedPricing={dateSelectionModal.updatedPricing}
        loadingPricing={dateSelectionModal.loadingPricing}
        onDateSelection={handleDateSelection}
        onProceedToBooking={proceedToBooking}
      />

      <BookingModal
        isOpen={bookingModal.isOpen}
        onClose={() => setBookingModal({
          isOpen: false, 
          roomType: null, 
          hotelName: '', 
          packageDetails: null, 
          activityDetails: null,
          itemName: '', 
          itemType: 'room',
          preSelectedDates: null
        })}
        roomType={bookingModal.roomType}
        hotelName={bookingModal.hotelName}
        packageDetails={bookingModal.packageDetails}
        activityDetails={bookingModal.activityDetails}
        itemName={bookingModal.itemName}
        itemType={bookingModal.itemType}
        preSelectedDates={bookingModal.preSelectedDates}
        onConfirmBooking={handleConfirmBooking}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={handlePaymentClose}
        bookingData={paymentModal.bookingData}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        stripePublishableKey={stripePublishableKey}
      />

      <BookingConfirmation
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, bookingData: null, paymentData: null })}
        bookingData={confirmationModal.bookingData}
        paymentData={confirmationModal.paymentData}
      />

      <div className="flex h-screen" style={{ background: 'linear-gradient(to bottom right, color-mix(in srgb, var(--widget-primary-color, #7c3aed) 5%, white), color-mix(in srgb, var(--widget-accent-color, var(--widget-primary-color, #4f46e5)) 5%, white))' }}>
        {/* Left Panel - Search and Packages */}
        <div className="w-1/2 bg-white border-r overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b">
          {/* Operator Logo & Company Name in Explore Mode */}
          {branding?.companyName && (
            <div className="w-full text-center py-3 border-b border-gray-100 mb-4">
              <div className="flex items-center justify-center gap-2">
                {branding.logoUrl && (
                  <img
                    src={branding.logoUrl}
                    alt={branding.companyName || 'Logo'}
                    className="h-8 w-auto object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <span className="text-sm font-semibold text-gray-700">
                  {branding.companyName}
                </span>
              </div>
            </div>
          )}
          <h1 className="text-3xl font-bold mb-4 text-gray-800 tracking-tight">
            {searchResults.length > 0 ? `Search Results (${allItems.length})` : `Explore Safari Packages (${allItems.length})`}
          </h1>

          {/* Navigation Tabs - Filter Options - Only show when we have results */}
          {allItems.length > 0 && (
            <div className="flex gap-2 mb-6 flex-wrap">
              <Button
                variant={activeFilter === "all" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "all" ? "text-white" : ""}
                style={activeFilter === "all" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("all")}
              >
                All
              </Button>
              <Button
                variant={activeFilter === "accommodation" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "accommodation" ? "text-white" : ""}
                style={activeFilter === "accommodation" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("accommodation")}
              >
                Lodges
              </Button>
              <Button
                variant={activeFilter === "activity" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "activity" ? "text-white" : ""}
                style={activeFilter === "activity" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("activity")}
              >
                Experiences
              </Button>
              <Button
                variant={activeFilter === "package" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "package" ? "text-white" : ""}
                style={activeFilter === "package" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("package")}
              >
                Packages
              </Button>
              <Button
                variant={activeFilter === "transfer" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "transfer" ? "text-white" : ""}
                style={activeFilter === "transfer" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("transfer")}
              >
                Transfers
              </Button>
              <Button
                variant={activeFilter === "guide" ? "default" : "outline"}
                size="sm"
                className={activeFilter === "guide" ? "text-white" : ""}
                style={activeFilter === "guide" ? brandStyles.primaryBg : undefined}
                onClick={() => setActiveFilter("guide")}
              >
                Guides
              </Button>
            </div>
          )}

        </div>

        {/* Package Grid */}
        <div className="p-6">
          {/* Empty State for Explore Section */}
          {allItems.length === 0 && (
            <div className="flex items-center justify-center min-h-[500px]">
              <div className="text-center max-w-lg mx-auto px-8">
                {isLoading ? (
                  /* Loading State */
                  <div className="space-y-6">
                    <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center animate-pulse" style={brandStyles.primaryBgLight15}>
                      <Search className="w-12 h-12" style={brandStyles.primaryText} />
                    </div>
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded animate-pulse mx-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse mx-16"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse mx-12"></div>
                    </div>
                    <p className="text-gray-600 text-lg">Searching for amazing experiences...</p>
                  </div>
                ) : !hasSearched ? (
                  /* Empty State - Never Searched */
                  <div className="space-y-6">
                    <div className="relative">
                      <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(to bottom right, color-mix(in srgb, var(--widget-primary-color, #7c3aed) 15%, white), color-mix(in srgb, var(--widget-accent-color, var(--widget-primary-color, #4f46e5)) 15%, white))' }}>
                        <MessageCircle className="w-16 h-16" style={brandStyles.primaryText} />
                      </div>
                      <div className="absolute -top-2 -right-8 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                        <span className="text-sm">✨</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-2xl font-bold text-gray-800">Start a Conversation</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        Switch to the <strong style={brandStyles.primaryText}>Chat</strong> section and describe your dream safari experience. 
                        Ask questions, share your preferences, and discover amazing packages tailored just for you!
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-full animate-pulse" style={{ ...brandStyles.primaryText, ...brandStyles.primaryBgLight15 }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                          </svg>
                          <span className="font-medium">Click Chat to begin</span>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={brandStyles.primaryBg}></div>
                        </div>
                      </div>
                    </div>
                    <div className="border rounded-xl p-6 space-y-3" style={{ ...brandStyles.primaryBgLight, ...brandStyles.primaryBorderLight }}>
                      <h4 className="font-semibold flex items-center gap-2" style={brandStyles.primaryText}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Try asking:
                      </h4>
                      <div className="space-y-2 text-sm" style={brandStyles.primaryText}>
                        <p>"Show me 5-day safari packages in Kenya"</p>
                        <p>"Find luxury lodges with game drives"</p>
                        <p>"What activities are available in Maasai Mara?"</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Empty State - No Results */
                  <div className="space-y-6">
                    <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6M3 12a9 9 0 1118 0 9 9 0 01-18 0z"/>
                      </svg>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-bold text-gray-800">No Results Found</h3>
                      <p className="text-gray-600">
                        We couldn't find any experiences matching your search. 
                        Try a different query or browse our available options.
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Tip:</strong> Try broader terms like "safari", "lodge", or "activities"
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results Grid - Only show when we have items */}
          {allItems.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {allItems.map((pkg) => (
              <Card
                key={pkg.id}
                className={`cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 hover:-translate-y-1 ${
                  selectedPackage === pkg.id ? "ring-2" : ""
                }`}
                style={selectedPackage === pkg.id ? { '--tw-ring-color': 'var(--widget-primary-color, #7c3aed)' } as React.CSSProperties : undefined}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                <CardContent className="p-0">
                  <div className="relative group">
                    {getCurrentImage(pkg) && !imageErrors[pkg.id] ? (
                      <>
                        <img
                          src={getCurrentImage(pkg)}
                          alt={pkg.title}
                          className="w-full h-48 object-cover rounded-t-lg transition-all duration-300"
                          onError={() => {
                            setImageErrors(prev => ({ ...prev, [pkg.id]: true }));
                          }}
                        />
                        
                        {/* Image Navigation Arrows - Only show if multiple images */}
                        {pkg.images && Array.isArray(pkg.images) && pkg.images.length > 1 && (
                          <>
                            {/* Previous Image Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-8 h-8 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigateImage(pkg.id, 'prev', pkg.images.length)
                              }}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                              </svg>
                            </Button>
                            
                            {/* Next Image Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-8 h-8 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigateImage(pkg.id, 'next', pkg.images.length)
                              }}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                              </svg>
                            </Button>
                            
                            {/* Image Counter */}
                            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                              {(currentImageIndexes[pkg.id] || 0) + 1}/{pkg.images.length}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-t-lg flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <div className="w-16 h-16 mx-auto mb-2 bg-gray-200 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9zM15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                          </div>
                          <span className="text-sm">No images available</span>
                        </div>
                      </div>
                    )}

                    {/* Package Type Badge */}
                    {(pkg.type === 'package' || pkg.content_type === 'package') && (
                      <div className="absolute top-2 left-2">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${getPackageTypeBadge(pkg).color} backdrop-blur-sm`}>
                          <span className="text-xs">{getPackageTypeBadge(pkg).icon}</span>
                          {getPackageTypeBadge(pkg).type}
                        </div>
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-white/80 hover:bg-white w-8 h-8 p-0 rounded-full"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavorite(pkg.id)
                      }}
                    >
                      <Heart
                        className={`w-4 h-4 ${
                          favorites.includes(pkg.id) ? "fill-red-500 text-red-500" : "text-gray-600"
                        }`}
                      />
                    </Button>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-xl mb-3 leading-tight">{pkg.title}</h3>
                    {/* Show description for hotels and packages, type/location for activities */}
                    {(pkg.type === 'accommodation' || pkg.content_type === 'accommodation') ? (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2 leading-relaxed">
                        {pkg.description || 'Comfortable accommodation with excellent amenities'}
                      </p>
                    ) : (pkg.type === 'package' || pkg.content_type === 'package') ? (
                      <div className="mb-4 space-y-4">
                        {/* Quick Info Icons Row */}
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span className="font-medium">{getPackageQuickInfo(pkg).duration}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                            </svg>
                            <span className="font-medium">{getPackageQuickInfo(pkg).groupSize}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                            </svg>
                            <span className="font-medium">{getPackageQuickInfo(pkg).difficulty}</span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className={`text-sm text-gray-600 leading-relaxed ${expandedDescriptions[pkg.id] ? '' : 'line-clamp-2'}`}>
                          {pkg.description || 'Experience an amazing adventure with this carefully crafted package that includes exciting activities and memorable experiences.'}
                        </p>
                        {pkg.description && pkg.description.length > 120 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleDescriptionExpansion(pkg.id)
                            }}
                            className="text-sm font-medium"
                            style={brandStyles.primaryText}
                          >
                            {expandedDescriptions[pkg.id] ? 'Show less' : 'Read more'}
                          </button>
                        )}

                        {/* Enhanced Price Display with CTA Button */}
                        <div className="flex items-center justify-between pt-3">
                          <div>
                            <div className="flex items-baseline gap-1">
                              <span className="text-sm text-gray-500 font-medium">from</span>
                              <span className="text-2xl font-bold text-gray-900">
                                ${pkg.metadata?.base_price || pkg.price?.replace(/[^\d]/g, '') || ''}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">per person</span>
                          </div>
                          <Button
                            size="sm"
                            className="text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                            style={brandStyles.primaryBg}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleBookPackage({
                                ...pkg.metadata,
                                price: pkg.metadata?.base_price || 0,
                                name: pkg.title
                              }, pkg.title)
                            }}
                          >
                            Book Now
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Enhanced Activity Information */}
                    {(pkg.type === 'activity' || pkg.content_type === 'activity') && pkg.metadata && (
                      <div className="space-y-2 mb-3">
                        {/* Duration */}
                        {pkg.metadata.duration_hours && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <span>{pkg.metadata.duration_hours} hours</span>
                          </div>
                        )}
                        
                        {/* Max Participants */}
                        {pkg.metadata.max_participants && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                            </svg>
                            <span>Up to {pkg.metadata.max_participants} people</span>
                          </div>
                        )}
                        
                        {/* Activity Type/Category */}
                        {pkg.metadata.category && (
                          <div className="inline-block bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded">
                            {pkg.metadata.category}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show description instead of price for activities */}
                    {(pkg.type === 'activity' || pkg.content_type === 'activity') && (
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                          {pkg.description || 'Experience an amazing activity with professional guidance and all necessary equipment included.'}
                        </p>
                        <div className="flex items-center justify-end">
                          <Button
                          size="sm"
                          className="text-white px-3 py-1 text-sm"
                          style={brandStyles.primaryBg}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleBookActivity({
                              ...pkg.metadata,
                              price: pkg.metadata?.base_price || 0,
                              name: pkg.title
                            }, pkg.title)
                          }}
                        >
                          Book Now
                        </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          )}

        </div>
      </div>

      {/* Right Panel - Package Details */}
      <div className="w-1/2 bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
              <Share2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
              <Heart className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Package Title - Hide for activities since they have custom header */}
        {selectedItem?.type !== 'activity' && selectedItem?.content_type !== 'activity' && allItems.length > 0 && (
          <div className="p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-800 tracking-tight">{selectedItem?.title || 'No Selection'}</h2>
            
            {/* Compact Hotel Details Cards */}
            <div className="grid grid-cols-2 gap-2 mt-4">
              {selectedItem?.type === 'accommodation' || selectedItem?.content_type === 'accommodation' ? (
                // Compact Hotel/Accommodation Details
                <>
                  {/* Star Rating Card */}
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-amber-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">{selectedItem?.metadata?.star_rating || selectedItem?.rating || ''}</span>
                        <span className="text-sm text-gray-600">Stars</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Rooms Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-blue-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">{selectedItem?.metadata?.total_rooms || selectedItem?.metadata?.room_count || (selectedItem as any)?.total_rooms || ''}</span>
                        <span className="text-sm text-gray-600">rooms</span>
                      </div>
                    </div>
                  </div>

                  {/* Location Card */}
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-emerald-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                      </div>
                      <div className="text-lg font-bold text-gray-900 truncate">
                        {selectedItem?.location || selectedItem?.metadata?.city || 'London'}
                      </div>
                    </div>
                  </div>

                  {/* Check-in Card */}
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-violet-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {selectedItem?.metadata?.check_in_time || '3:00 PM'}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                // Modern Package/Activity Details Cards
                <>
                  {/* Duration Card */}
                  <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-rose-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 012 0v4h2V3a1 1 0 012 0v4h2a1 1 0 011 1v10a3 3 0 01-3 3H6a3 3 0 01-3-3V8a1 1 0 011-1h2z"/>
                        </svg>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">
                          {selectedItem?.metadata?.duration_days || selectedItem?.metadata?.duration || ''}
                        </span>
                        <span className="text-sm text-gray-600">days</span>
                      </div>
                    </div>
                  </div>

                  {/* Max Travelers Card */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-blue-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                        </svg>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">
                          {selectedItem?.metadata?.max_travelers || selectedItem?.metadata?.capacity || ''}
                        </span>
                        <span className="text-sm text-gray-600">people</span>
                      </div>
                    </div>
                  </div>

                  {/* Package Type/Difficulty Card */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-amber-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                        </svg>
                      </div>
                      <div className="text-lg font-bold text-gray-900 truncate">
                        {selectedItem?.metadata?.difficulty || selectedItem?.metadata?.package_type || 'Adventure'}
                      </div>
                    </div>
                  </div>

                  {/* Price Per Person Card */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 hover:shadow-sm transition-all h-16">
                    <div className="flex items-center justify-center gap-2 h-full">
                      <div className="bg-green-100 p-1.5 rounded-md">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                        </svg>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">
                          ${selectedItem?.metadata?.base_price || selectedItem?.price || ''}
                        </span>
                        <span className="text-sm text-gray-600">/person</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {allItems.length === 0 && (
            /* Empty state for right panel when no items */
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400 max-w-sm">
                <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No packages to explore</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Start a conversation in the <strong style={brandStyles.primaryText}>Chat</strong> section to discover amazing safari experiences and see details here.
                </p>
              </div>
            </div>
          )}
          
          {allItems.length > 0 && selectedItem?.type === 'accommodation' ? (
            // Hotel Room Types Display
            <div className="space-y-4">
              
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-800">Available Room Types</h3>
                {loadingRooms && <span className="text-xs text-gray-500 font-normal">Loading...</span>}
              </div>
              
              {roomTypes.length > 0 ? (
                <div className="space-y-3">
                  {roomTypes.map((roomType, index) => {
                    const roomKey = `${roomType.id || index}`;
                    const isExpanded = expandedDescriptions[`room_${roomKey}`];
                    const description = roomType.description || '';
                    const shouldShowExpand = description.length > 120;
                    const displayDescription = shouldShowExpand && !isExpanded 
                      ? description.substring(0, 120) + '...' 
                      : description;
                    
                    return (
                    <div key={roomType.id || index} className="border border-gray-200 rounded-lg p-3 hover:border-gray-400 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{roomType.name}</h4>
                          <div className="mt-1">
                            <p className="text-xs text-gray-600 leading-relaxed">{displayDescription}</p>
                            {shouldShowExpand && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedDescriptions(prev => ({
                                    ...prev,
                                    [`room_${roomKey}`]: !prev[`room_${roomKey}`]
                                  }));
                                }}
                                className="text-xs mt-1 font-medium focus:outline-none"
                                style={brandStyles.primaryText}
                              >
                                {isExpanded ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
                              </svg>
                              <span className="font-normal">Max {roomType.max_occupancy} guests</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-3 h-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v0" transform="translate(0,1)"/>
                                <rect width="18" height="4" x="3" y="11" rx="1"/>
                              </svg>
                              <span className="font-normal">{roomType.bed_configuration}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"/>
                              </svg>
                              <span className="font-normal">{roomType.room_size}m²</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-3">
                          <div className="text-base font-semibold text-gray-900">${roomType.base_price}</div>
                          <div className="text-xs text-gray-500 font-normal">per night</div>
                        </div>
                      </div>
                      
                      {/* Room Amenities */}
                      {roomType.amenities && roomType.amenities.length > 0 && (
                        <div className="mb-2">
                          <div className="flex flex-wrap gap-1">
                            {roomType.amenities.slice(0, 3).map((amenity: string, i: number) => (
                              <span key={i} className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded font-normal">
                                {amenity}
                              </span>
                            ))}
                            {roomType.amenities.length > 3 && (
                              <span className="text-xs text-gray-500 font-normal">+{roomType.amenities.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-500 font-normal">{roomType.total_rooms} rooms available</span>
                        <Button 
                          className="text-white px-4 py-1.5 rounded-md text-xs font-medium"
                          style={brandStyles.primaryBg}
                          onClick={() => handleBookRoom(roomType, selectedItem?.title || 'Hotel')}
                        >
                          Book Room
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : !loadingRooms ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No room types available for this hotel.</p>
                </div>
              ) : null}
            </div>
          ) : allItems.length > 0 && (selectedItem?.type === 'package' || selectedItem?.content_type === 'package') ? (
            // Package Details Display
            <div className="space-y-6 text-sm">
              {loadingPackage ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading package details...</p>
                </div>
              ) : packageDetails ? (
                <>


                  {/* Day-wise Itinerary */}
                  {packageDetails?.components && packageDetails.components.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2"><svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                      </svg>
                      Day-wise Itinerary</h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {packageDetails.components.map((component: any, index: number) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-3 bg-white">
                            <div className="flex items-start gap-3">
                              <div className="rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold" style={{ ...brandStyles.primaryBgLight15, ...brandStyles.primaryText }}>
                                {component.day_number || index + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-800 mb-1">
                                  Day {component.day_number || index + 1}: {component.component_name || component.component_type}
                                </h4>
                                {component.description && (
                                  <p className="text-sm text-gray-600 mb-2">{component.description}</p>
                                )}
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  {component.start_time && (
                                    <span>🕐 {component.start_time}</span>
                                  )}
                                  {component.component_type && (
                                    <span className="bg-gray-100 px-2 py-1 rounded capitalize">
                                      {component.component_type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Inclusions */}
                  {packageDetails?.includes && packageDetails.includes.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      What's Included</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {packageDetails.includes
                          .filter((inclusion: string) =>
                            inclusion &&
                            inclusion.trim().length > 0 &&
                            inclusion.trim().length < 200 &&
                            inclusion !== packageDetails.description
                          )
                          .map((inclusion: string, index: number) => (
                          <div key={index} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                            <span className="text-gray-700">{inclusion}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Exclusions */}
                  {packageDetails?.excludes && packageDetails.excludes.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                      </svg>
                      Not Included</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {packageDetails.excludes.map((exclusion: string, index: number) => (
                          <div key={index} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                            </svg>
                            <span className="text-gray-700">{exclusion}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Terms & Conditions */}
                  {packageDetails?.terms_conditions && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-3">Terms & Conditions</h3>
                      <p className="text-gray-700 text-sm leading-relaxed bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                        {packageDetails.terms_conditions}
                      </p>
                    </div>
                  )}

                  {/* Day-wise Itinerary */}
                  <div className="pt-6 border-t-2" style={brandStyles.primaryBorderLight}>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5" style={brandStyles.primaryText} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 012 0v4h2V3a1 1 0 012 0v4h2a1 1 0 011 1v10a3 3 0 01-3 3H6a3 3 0 01-3-3V8a1 1 0 011-1h2z"/>
                      </svg>
                      Day-wise Itinerary
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {packageDetails?.itinerary && packageDetails.itinerary.length > 0 ? (
                        packageDetails.itinerary.map((day: any, index: number) => (
                          <div key={index} className="rounded-lg p-4 border" style={{ background: 'linear-gradient(to right, color-mix(in srgb, var(--widget-primary-color, #7c3aed) 5%, white), color-mix(in srgb, #ec4899 5%, white))', ...brandStyles.primaryBorderLight }}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-white text-sm font-bold px-2 py-1 rounded-full" style={brandStyles.primaryBg}>
                                Day {index + 1}
                              </span>
                              <h4 className="font-semibold text-gray-800">{day.title || `Day ${index + 1}`}</h4>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {day.description || day.activities || 'Exciting activities planned for this day'}
                            </p>
                            {day.meals && (
                              <div className="mt-2 text-sm font-medium" style={brandStyles.primaryText}>
                                🍽️ Meals: {day.meals}
                              </div>
                            )}
                            {day.accommodation && (
                              <div className="mt-1 text-sm text-green-600 font-medium">
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                  </svg>
                                  Stay: {day.accommodation}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <div className="flex justify-center mb-3">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                            </svg>
                          </div>
                          <p>Detailed itinerary will be provided upon booking</p>
                        </div>
                      )}
                    </div>

                    {/* Pricing and Book Now Button */}
                    <div className="mt-6 space-y-3">
                      <Button
                        className="w-full text-white py-5 rounded-xl text-lg font-bold shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 relative overflow-hidden"
                        style={brandStyles.primaryBg}
                        onClick={() => handleBookPackage({
                          ...packageDetails,
                          price: packageDetails?.base_price || (selectedItem as any)?.metadata?.base_price || 0,
                          name: selectedItem?.title || packageDetails?.name
                        }, selectedItem?.title || packageDetails.name)}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                          </svg>
                          Book This Package Now
                        </div>
                      </Button>
                      <p className="text-sm text-center text-gray-500">
                        <div className="flex items-center justify-center gap-4 flex-wrap">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                            </svg>
                            <span>Secure booking</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                            </svg>
                            <span>24/7 support</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                            </svg>
                            <span>Best price guarantee</span>
                          </div>
                        </div>
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                // Fallback package display when API details aren't available
                <>
                  {/* Package Pricing */}
                  <div className="p-4 rounded-lg border" style={{ background: 'linear-gradient(to right, color-mix(in srgb, var(--widget-primary-color, #7c3aed) 5%, white), color-mix(in srgb, var(--widget-accent-color, var(--widget-primary-color, #4f46e5)) 5%, white))', ...brandStyles.primaryBorderLight }}>
                    <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center gap-2"><svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                      </svg>
                      Pricing</h3>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Starting from:</span>
                        <span className="text-2xl font-bold" style={brandStyles.primaryText}>{selectedItem?.price || 'Contact for pricing'}</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-2">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                          </svg>
                          Final price may vary based on selected dates, group size, and optional add-ons
                        </div>
                      </div>
                    </div>
                  </div>



                  {/* Standard Inclusions for Safari Packages */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3"><svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      What's Typically Included</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {[
                        'Professional safari guide',
                        'Game drive vehicle with pop-up roof',
                        'Park entry fees',
                        'Bottled water during game drives',
                        'Binoculars for wildlife viewing',
                        'Accommodation as specified',
                        'Meals as per itinerary',
                        'Airport transfers'
                      ].map((inclusion: string, index: number) => (
                        <div key={index} className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                          <span className="text-gray-700">{inclusion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Typical Exclusions */}
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3"><svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                      </svg>
                      Not Included</h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {[
                        'International flights',
                        'Travel insurance',
                        'Alcoholic beverages',
                        'Personal expenses and tips',
                        'Optional activities not mentioned',
                        'Visa fees'
                      ].map((exclusion: string, index: number) => (
                        <div key={index} className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                          <span className="text-gray-700">{exclusion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Day-wise Itinerary */}
                  <div className="pt-6 border-t-2" style={brandStyles.primaryBorderLight}>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5" style={brandStyles.primaryText} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 012 0v4h2V3a1 1 0 012 0v4h2a1 1 0 011 1v10a3 3 0 01-3 3H6a3 3 0 01-3-3V8a1 1 0 011-1h2z"/>
                      </svg>
                      Day-wise Itinerary
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedItem?.metadata?.itinerary && selectedItem.metadata.itinerary.length > 0 ? (
                        selectedItem.metadata.itinerary.map((day: any, index: number) => (
                          <div key={index} className="rounded-lg p-4 border" style={{ background: 'linear-gradient(to right, color-mix(in srgb, var(--widget-primary-color, #7c3aed) 5%, white), color-mix(in srgb, #ec4899 5%, white))', ...brandStyles.primaryBorderLight }}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-white text-sm font-bold px-2 py-1 rounded-full" style={brandStyles.primaryBg}>
                                Day {index + 1}
                              </span>
                              <h4 className="font-semibold text-gray-800">{day.title || `Day ${index + 1}`}</h4>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {day.description || day.activities || 'Exciting activities planned for this day'}
                            </p>
                            {day.meals && (
                              <div className="mt-2 text-sm font-medium" style={brandStyles.primaryText}>
                                🍽️ Meals: {day.meals}
                              </div>
                            )}
                            {day.accommodation && (
                              <div className="mt-1 text-sm text-green-600 font-medium">
                                <div className="flex items-center gap-1">
                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                                  </svg>
                                  Stay: {day.accommodation}
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <div className="flex justify-center mb-3">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                            </svg>
                          </div>
                          <p>Detailed itinerary will be provided upon booking</p>
                        </div>
                      )}
                    </div>

                    {/* Pricing and Book Now Button */}
                    <div className="mt-6 space-y-3">
                      <Button
                        className="w-full text-white py-5 rounded-xl text-lg font-bold shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 relative overflow-hidden"
                        style={brandStyles.primaryBg}
                        onClick={() => handleBookPackage({
                          ...selectedItem?.metadata,
                          price: selectedItem?.metadata?.base_price || 0,
                          name: selectedItem?.title || 'Safari Package',
                          description: selectedItem?.description
                        }, selectedItem?.title || 'Safari Package')}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                          </svg>
                          Book This Package Now
                        </div>
                      </Button>
                      <p className="text-sm text-center text-gray-500">
                        <div className="flex items-center justify-center gap-4 flex-wrap">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                            </svg>
                            <span>Secure booking</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                            </svg>
                            <span>24/7 support</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                            </svg>
                            <span>Best price guarantee</span>
                          </div>
                        </div>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : selectedItem?.type === 'activity' || selectedItem?.content_type === 'activity' ? (
            // Activity Details Display - New Design
            <div className="flex flex-col h-full">
              {loadingActivity ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading activity details...</p>
                </div>
              ) : activityDetails ? (
                <>
                  {/* Hero Image */}
                  <div className="relative h-48 bg-gray-100 rounded-t-lg overflow-hidden">
                    <img
                      src={getImageFromSearchResult({
                        details: { images: activityDetails.images || [] },
                        metadata: { images: activityDetails.images || [] }
                      }) || selectedItem?.image}
                      alt={selectedItem?.title || activityDetails.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.nextElementSibling as HTMLElement;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                    {/* Placeholder for broken images */}
                    <div className="absolute inset-0 bg-gray-200 flex items-center justify-center" style={{ display: 'none' }}>
                      <div className="text-center text-gray-400">
                        <div className="w-16 h-16 mx-auto mb-2 bg-gray-300 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9zM15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                          </svg>
                        </div>
                        <span className="text-sm">No image available</span>
                      </div>
                    </div>
                  </div>

                  {/* Title Header */}
                  <div className="p-4 border-b bg-white">
                    <div className="mb-2">
                      <h1 className="text-lg font-bold text-gray-900">
                        {selectedItem?.title || activityDetails.name}
                      </h1>
                    </div>
                    
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 p-4 bg-white overflow-y-auto">
                    <div className="grid grid-cols-1 gap-6">
                      {/* What's Included */}
                      <div>
                        <h2 className="font-semibold text-gray-800 mb-3">What's Included</h2>
                        <div className="space-y-2">
                          {activityDetails.includes && activityDetails.includes.length > 0 ? (
                            activityDetails.includes.map((inclusion: string, index: number) => (
                              <div key={index} className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">{inclusion}</span>
                              </div>
                            ))
                          ) : (
                            // Default inclusions for demo
                            <>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">4-course fine dining dinner</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">Welcome drink on arrival</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">Live jazz band performance</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">Professional photography service</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-gray-700 text-sm">Commentary on London landmarks</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Activity Details Grid */}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-6 pt-6 border-t">
                      <div>
                        <span className="text-sm text-gray-500">Duration</span>
                        <div className="text-gray-900 font-medium">{activityDetails.duration_hours || 3} hours</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Max Participants</span>
                        <div className="text-gray-900 font-medium">{activityDetails.max_participants || 120} people</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Difficulty</span>
                        <div className="text-gray-900 font-medium capitalize">{activityDetails.difficulty_level || 'Easy'}</div>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Min Age</span>
                        <div className="text-gray-900 font-medium">{activityDetails.min_age || 0}+ years</div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Pricing and Booking */}
                  <div className="bg-white border-t p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-sm text-gray-500">Starting from</div>
                        <div className="text-2xl font-bold text-gray-900">
                          ${activityDetails.price || activityDetails.base_price || 45}
                          <span className="text-sm font-normal text-gray-500 ml-1">per person</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Next available</div>
                        <div className="text-sm font-medium text-gray-900">Today, 7:00 PM</div>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full text-white py-3 rounded-lg font-semibold"
                      style={brandStyles.primaryBg}
                      onClick={() => handleBookActivity(activityDetails, selectedItem?.title || activityDetails.name)}
                    >
                      Reserve Your Spot
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No activity details available.</p>
                </div>
              )}
            </div>
          ) : allItems.length > 0 ? (
            // Other non-hotel items (other types, etc.)
            <div className="grid grid-cols-1 gap-4 text-sm">
              {/* Description */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800 mb-3">Description:</h3>
                <p className="text-gray-700">{selectedItem?.description}</p>
              </div>

              {/* Features/Amenities */}
              {selectedItem?.amenities && selectedItem.amenities.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h3 className="font-semibold text-gray-800 mb-3">What's Included:</h3>
                  {selectedItem.amenities.map((amenity: string, index: number) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">•</span>
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Additional Info */}
              <div className="mt-6 space-y-3">
                <h3 className="font-semibold text-gray-800 mb-3">Additional Information:</h3>
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                  </svg>
                  <span>Type: {selectedItem?.type}</span>
                </div>
                {selectedItem?.metadata?.star_rating && (
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                    <span>{selectedItem.metadata.star_rating} Star Rating</span>
                  </div>
                )}
              </div>

              {/* Book Now Button for fallback items */}
              <div className="pt-4 border-t">
                <Button 
                  className="w-full text-white py-3 rounded-lg text-base font-semibold"
                  style={brandStyles.primaryBg}
                  onClick={() => {
                    if (selectedItem?.type?.toLowerCase().includes('package') || selectedItem?.content_type?.toLowerCase().includes('package')) {
                      handleBookPackage({
                        ...selectedItem?.metadata,
                        price: selectedItem?.metadata?.base_price || 0,
                        name: selectedItem?.title || 'Package',
                        description: selectedItem?.description
                      }, selectedItem?.title || 'Package');
                    } else if (selectedItem?.type?.toLowerCase().includes('activity') || selectedItem?.content_type?.toLowerCase().includes('activity')) {
                      handleBookActivity({
                        ...selectedItem?.metadata,
                        price: selectedItem?.metadata?.base_price || 0,
                        name: selectedItem?.title || 'Activity',
                        description: selectedItem?.description
                      }, selectedItem?.title || 'Activity');
                    } else {
                      // Default to package booking for safari items
                      handleBookPackage({
                        ...selectedItem?.metadata,
                        price: selectedItem?.metadata?.base_price || 0,
                        name: selectedItem?.title || 'Safari Package',
                        description: selectedItem?.description
                      }, selectedItem?.title || 'Safari Package');
                    }
                  }}
                >
                  Book Now
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      </div>
    </>
  )
}

// Widget Configuration Interface
interface SalesCard {
  title: string
  description: string
  icon: string
  color: string
  search_query: string
}

interface DynamicWidgetConfig {
  operator_type: 'hotel' | 'tour' | 'activity' | 'safari'
  widget_header: string
  widget_subtitle: string
  sales_cards: SalesCard[]
}

// EXACT Modern Search Widget from your original code
interface ExactModernWidgetProps extends WidgetConfig {
  containerId?: string;
}

const ExactModernWidget: React.FC<ExactModernWidgetProps> = (componentProps) => {
  // Use a different variable name to avoid any potential conflicts
  const {
    tenantId,
    onSearch,
    onBooking,
    operatorType,
    propertyInfo,
    businessInfo,
    onError,
    branding,
    containerId,
    stripePublishableKey,
    ...restProps
  } = componentProps;
  const [searchQuery, setSearchQuery] = useState("")
  const [isListening, setIsListening] = useState(false)
  const [activeMode, setActiveMode] = useState<"chat" | "explore" | "guides">("chat")
  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<NormalizedSearchResult[]>([])
  const [crawledContent, setCrawledContent] = useState<any[]>([])

  // Auto-switch away from Guides tab when crawled content disappears
  useEffect(() => {
    if (activeMode === "guides" && crawledContent.length === 0) {
      setActiveMode("explore");
    }
  }, [crawledContent, activeMode]);
  const [hasSearched, setHasSearched] = useState(false)
  const [widgetConfig, setWidgetConfig] = useState<DynamicWidgetConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [fetchedBranding, setFetchedBranding] = useState<WidgetBranding | null>(null)

  const widgetContainerId = containerId || `exact-modern-widget-${tenantId}`

  const activeBranding = branding || fetchedBranding;

  // Apply branding to widget container
  useEffect(() => {
    const activeBranding = branding || fetchedBranding;
    if (!activeBranding) return;
    applyBranding(activeBranding, widgetContainerId);
    if (activeBranding.customCSS) {
      injectCustomCSS(activeBranding.customCSS, widgetContainerId);
    }
  }, [branding, fetchedBranding, widgetContainerId]);

  // Auto-inject Schema.org JSON-LD into the host page's <head>
  useEffect(() => {
    if (!tenantId) return;
    const SCRIPT_ID = 'atlasiq-schema-jsonld';
    // Don't inject twice
    if (document.getElementById(SCRIPT_ID)) return;

    const inject = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/schema/by-tenant?tenant_id=${tenantId}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (!data?.['@graph']?.length) return;
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      } catch {
        // Silent — schema injection is non-critical
      }
    };
    inject();

    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [tenantId]);

  // Fetch branding from API if not provided via props
  useEffect(() => {
    if (branding) return;
    if (!tenantId) return;

    const fetchBranding = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/widget/branding/${tenantId}`);
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.branding) {
          setFetchedBranding({
            primaryColor: data.branding.primaryColor,
            secondaryColor: data.branding.secondaryColor,
            accentColor: data.branding.accentColor,
            textColor: data.branding.textColor,
            backgroundColor: data.branding.backgroundColor,
            logoUrl: data.branding.logoUrl,
            companyName: data.branding.companyName,
          });
        }
      } catch (err) {
        console.warn('Failed to load operator branding:', err);
      }
    };

    fetchBranding();
  }, [tenantId, branding]);

  // Fetch widget configuration on component mount
  useEffect(() => {
    const fetchWidgetConfig = async () => {
      try {
        setIsLoadingConfig(true)
        const response = await fetch(`${API_BASE_URL}/api/widget-config?tenant_id=${tenantId}`)
        const data = await response.json()
        
        if (data.success && data.widget_config) {
          setWidgetConfig(data.widget_config)
        } else {
          // Fallback to default safari configuration if API fails
          setWidgetConfig({
            operator_type: 'safari',
            widget_header: 'Plan Your Safari Adventure',
            widget_subtitle: 'Ask me anything about Safari Bookings or describe your dream safari experience',
            sales_cards: [
              {
                title: 'Best Time to Visit',
                description: 'When should I plan my safari?',
                icon: '📅',
                color: 'bg-blue-100 text-blue-600',
                search_query: 'best time to visit'
              },
              {
                title: 'Group Packages',
                description: 'Safari options for families',
                icon: '👥',
                color: 'bg-green-100 text-green-600',
                search_query: 'group packages'
              },
              {
                title: 'Luxury Experiences',
                description: 'Premium safari packages',
                icon: '🏆',
                color: 'bg-purple-100 text-purple-600',
                search_query: 'luxury experiences'
              }
            ]
          })
        }
      } catch (error) {
        console.error('Failed to fetch widget config:', error)
        // Use default configuration on error
        setWidgetConfig({
          operator_type: 'safari',
          widget_header: 'Plan Your Safari Adventure',
          widget_subtitle: 'Ask me anything about Safari Bookings or describe your dream safari experience',
          sales_cards: [
            {
              title: 'Best Time to Visit',
              description: 'When should I plan my safari?',
              icon: '📅',
              color: 'bg-blue-100 text-blue-600',
              search_query: 'best time to visit'
            },
            {
              title: 'Group Packages',
              description: 'Safari options for families',
              icon: '👥',
              color: 'bg-green-100 text-green-600',
              search_query: 'group packages'
            },
            {
              title: 'Luxury Experiences',
              description: 'Premium safari packages',
              icon: '🏆',
              color: 'bg-purple-100 text-purple-600',
              search_query: 'luxury experiences'
            }
          ]
        })
      } finally {
        setIsLoadingConfig(false)
      }
    }

    if (tenantId) {
      fetchWidgetConfig()
    }
  }, [tenantId])

  // Helper function to map icon strings to Lucide icons
  const getIconComponent = (iconString: string) => {
    const iconMap: { [key: string]: any } = {
      '📅': Calendar,
      '👥': Users,
      '🏆': Award,
      '🔍': Search,
      '🏨': Calendar, // Hotel fallback to calendar
      '🗺️': Search, // Tour fallback to search
      '🎯': Award, // Activity fallback to award
      '🦁': Award, // Safari fallback to award
      '💰': Award,
      '🌟': Award,
      '📞': Calendar,
      '📧': Search,
      '🎊': Award
    }
    
    return iconMap[iconString] || Search // Default to Search icon
  }

  const handleSearch = async (query: string) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    setHasSearched(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: query,
          tenant_id: tenantId
        })
      });

      const data = await response.json();

      // Check if we have structured search results
      const rawResults = data.data?.results || data.results || [];

      // Filter out crawled_content before normalization -- these are web page
      // chunks that lack inventory fields (name, price, images) and would
      // render as broken "Unknown Item" cards in the Explore tab.
      // Check both top-level and metadata.content_type for robustness.
      const inventoryResults = rawResults.filter(
        (item: any) => {
          const ct = item.content_type || item.metadata?.content_type || item.type || '';
          return ct !== 'crawled_content';
        }
      );

      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.log('[ExactModernWidget] Search filter:', { raw: rawResults.length, filtered: inventoryResults.length });

      // Save crawled content separately for the Guides tab
      // Only include items that have actual displayable text content
      const crawledItems = rawResults.filter(
        (item: any) => {
          const ct = item.content_type || item.metadata?.content_type || '';
          if (ct !== 'crawled_content') return false;
          const text = (item.text || item.details?.description || item.metadata?.text || '').trim();
          return text.length > 0;
        }
      );
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.log('[ExactModernWidget] Crawled content:', { total: crawledItems.length });
      setCrawledContent(crawledItems);

      // Normalize search results once at fetch boundary (parse JSON strings)
      const normalizedResults = normalizeSearchResults(inventoryResults);

      if (onSearch) {
        // Cast to any[] for callback compatibility - consumer may expect original format
        onSearch(query, normalizedResults as unknown as SearchResult[]);
      }

      // Store normalized search results in state
      setSearchResults(normalizedResults);

      // Switch to explore mode to show results
      setActiveMode("explore");
    } catch (error) {
      console.error('Search error:', error);
      // Clear search results on error
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div id={widgetContainerId} className="min-h-screen bg-white">
      {/* Operator Logo & Company Name */}
      {activeBranding?.logoUrl && (
        <div className="flex items-center justify-center gap-3 pt-6 pb-2">
          <img
            src={activeBranding.logoUrl}
            alt={activeBranding.companyName || 'Operator logo'}
            className="h-10 w-auto object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {activeBranding.companyName && (
            <span className="text-lg font-semibold text-gray-800">
              {activeBranding.companyName}
            </span>
          )}
        </div>
      )}
      {/* Toggle Buttons - Always Visible */}
      <div className="container mx-auto px-6 pt-8">
        <div className="max-w-4xl mx-auto mb-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center bg-gradient-to-r from-gray-100 to-gray-50 p-1.5 rounded-2xl gap-1 shadow-inner border border-gray-200">
              {[
                { mode: "chat" as const, label: "Chat", icon: MessageCircle },
                { mode: "explore" as const, label: "Explore", icon: Search, badge: searchResults.length > 0 ? searchResults.length : undefined },
                ...(crawledContent.length > 0 ? [{
                  mode: "guides" as const,
                  label: "Guides",
                  icon: BookOpen,
                  badge: crawledContent.length,
                }] : []),
              ].map((tab) => (
                <Button
                  key={tab.mode}
                  onClick={() => setActiveMode(tab.mode)}
                  className={`relative z-10 flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 border-0 hover:scale-105 active:scale-95 ${
                    activeMode === tab.mode
                      ? "text-white shadow-lg"
                      : "bg-transparent text-gray-600 hover:text-gray-800 hover:bg-white/50"
                  }`}
                  style={activeMode === tab.mode ? brandStyles.primaryGradient : undefined}
                >
                  <tab.icon className={`w-4 h-4 transition-transform duration-300 ${activeMode === tab.mode ? "rotate-12" : ""}`} />
                  <span>{tab.label}</span>
                  {'badge' in tab && tab.badge && tab.badge > 0 && (
                    <span className={`text-xs ml-0.5 ${activeMode === tab.mode ? "text-white/80" : "opacity-60"}`}>
                      {tab.badge > 9 ? '9+' : tab.badge}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Content Area with Transitions */}
      <div className="relative overflow-hidden">
        {/* Explore Mode */}
        <div className={`transition-all duration-700 ease-in-out ${
          activeMode === "explore" 
            ? "opacity-100 translate-x-0 relative z-10" 
            : "opacity-0 translate-x-full absolute inset-0 z-0 pointer-events-none"
        }`}>
          <SafariExplorePage onBooking={onBooking} searchResults={searchResults} crawledContent={crawledContent} tenantId={tenantId} hasSearched={hasSearched} isLoading={isLoading} branding={activeBranding} stripePublishableKey={stripePublishableKey} />
        </div>

        {/* Guides Mode */}
        <div className={`transition-all duration-700 ease-in-out ${
          activeMode === "guides"
            ? "opacity-100 translate-x-0 relative z-10"
            : "opacity-0 translate-x-full absolute inset-0 z-0 pointer-events-none"
        }`}>
          <GuidesPage
            crawledContent={crawledContent}
            searchQuery={searchQuery}
            isLoading={isLoading}
            onBrowsePackages={(topic) => {
              setSearchQuery(topic);
              setActiveMode("explore");
              handleSearch(topic);
            }}
            brandStyles={brandStyles}
          />
        </div>

        {/* Chat Mode */}
        <div className={`transition-all duration-700 ease-in-out ${
          activeMode === "chat"
            ? "opacity-100 translate-x-0 relative z-10"
            : "opacity-0 -translate-x-full absolute inset-0 z-0 pointer-events-none"
        }`}>
          {/* Chat content wrapped in transition container */}
          <div className="container mx-auto px-6 py-16">
            {/* Search Widget */}
            <div className="max-w-4xl mx-auto">
              <Card className="border shadow-2xl rounded-3xl overflow-hidden" style={{ ...brandStyles.primaryBgLight, ...brandStyles.primaryBorderLight }}>
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={brandStyles.primaryBg}>
                      <MessageCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {isLoadingConfig ? 'Loading...' : (widgetConfig?.widget_header || 'Plan Your Safari Adventure')}
                    </h2>
                    <p className="text-gray-600">
                      {isLoadingConfig ? 'Preparing your experience...' : (widgetConfig?.widget_subtitle || 'Ask me anything about Safari Bookings or describe your dream safari experience')}
                    </p>
                  </div>

                  {/* Search Input */}
                  <div className="relative mb-6">
                    <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border-2 border-transparent widget-focus-within transition-all duration-300 shadow-sm">
                      <Search className="w-5 h-5 text-gray-400" />
                      <Input
                        placeholder="Tell me about your ideal safari experience..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 border-0 bg-transparent text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none p-0 text-lg"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && searchQuery.trim() && !isLoading) {
                            handleSearch(searchQuery.trim());
                          }
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setIsListening(!isListening)}
                          className={`w-10 h-10 p-0 rounded-xl transition-colors ${
                            isListening
                              ? "bg-red-100 text-red-600"
                              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          <Mic className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="text-white w-10 h-10 p-0 rounded-xl shadow-lg"
                          style={brandStyles.primaryBg}
                          disabled={isLoading}
                          onClick={() => {
                            if (searchQuery.trim() && !isLoading) {
                              handleSearch(searchQuery.trim());
                            }
                          }}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Quick Suggestions */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {isLoadingConfig ? (
                      // Loading state - show skeleton cards
                      Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="h-auto p-4 rounded-2xl bg-white/30 animate-pulse">
                          <div className="w-10 h-10 rounded-xl bg-gray-200 mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      ))
                    ) : (
                      // Dynamic sales cards from configuration
                      (widgetConfig?.sales_cards || []).slice(0, 3).map((card, index) => {
                        const IconComponent = getIconComponent(card.icon)
                        return (
                          <Button
                            key={index}
                            variant="ghost"
                            className="h-auto p-4 flex flex-col items-start gap-2 text-left hover:bg-white/50 rounded-2xl transition-all duration-300 group bg-white/30"
                            onClick={() => {
                              const query = card.search_query || card.description
                              setSearchQuery(query);
                              handleSearch(query);
                            }}
                          >
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.color || 'bg-blue-100 text-blue-600'} group-hover:scale-110 transition-transform duration-300`}
                            >
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-sm">{card.title}</div>
                              <div className="text-gray-600 text-sm">{card.description}</div>
                            </div>
                          </Button>
                        )
                      })
                    )}
                  </div>

                  {/* Status Indicator */}
                  <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t" style={brandStyles.primaryBorderLight}>
                    <div className={`w-2 h-2 rounded-full ${isLoading ? 'bg-orange-500' : 'bg-green-500'} animate-pulse`}></div>
                    <span className="text-gray-600 text-sm">
                      {isLoading ? 'Processing your request...' : 'AI Safari Assistant is online'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExactModernWidget;